import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { SearchableSelect } from './ui/searchable-select';
import axios from 'axios';
import { Building, Plus, Search, MapPin, Users, Euro, ArrowUpDown, ChevronLeft, ChevronRight, Edit2, Eye, EyeOff, Link, RefreshCw, Download, DownloadCloud, FileText, Loader2, Shield, ShieldCheck, ShieldOff, Copy, Trash2, Merge, AlertTriangle, CheckCircle, Archive, ArchiveRestore, Calendar, X, Pause, Play, Mail } from 'lucide-react';
import { BACKEND_URL } from '../utils/config';
import { toast } from 'sonner';
import Ross1000BackfillSection from './Ross1000BackfillSection';
import { useAuth } from '../App';

const API = `${BACKEND_URL}/api`;

const VetrinaSlotsControl = () => {
  const [slots, setSlots] = useState(null);
  const [inVetrina, setInVetrina] = useState(0);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/site-vetrina-settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('hw_token')}` }
        });
        setSlots(r.data.slots);
        setInVetrina(r.data.in_vetrina);
      } catch (e) { /* ruoli senza permesso: controllo nascosto */ }
    })();
  }, []);
  if (slots === null) return null;
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/site-vetrina-settings`, { slots }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('hw_token')}` }
      });
      toast.success(`Vetrina sito: ${slots} posti`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio posti vetrina');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-1"
      data-testid="vetrina-slots-control"
      title="Immobili in vetrina sul sito: i nuovi immobili entrano da soli, i più vecchi escono a rotazione">
      <span className="text-sm text-slate-600 whitespace-nowrap">Vetrina: {inVetrina} /</span>
      <input type="number" min="1" max="100" value={slots}
        onChange={e => setSlots(parseInt(e.target.value) || slots)}
        className="w-14 h-8 border border-slate-200 rounded px-2 text-sm"
        data-testid="vetrina-slots-input" />
      <span className="text-sm text-slate-600">posti</span>
      <Button size="sm" variant="outline" disabled={saving} onClick={save} data-testid="vetrina-slots-save">
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Salva'}
      </Button>
    </div>
  );
};

// Helper riutilizzabile: mostra una data con badge "certa" / "≈ stimata"
const CredDate = ({ label, value, estimated, showBadge = true }) => {
  if (!value) return null;
  let formatted = value;
  try {
    formatted = new Date(value).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch (e) { /* lascia il valore grezzo */ }
  return (
    <div className="flex items-center gap-2 text-xs text-slate-600">
      <span className="font-medium text-slate-500">{label}:</span>
      <span className="text-slate-700">{formatted}</span>
      {showBadge && (
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${estimated ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
          {estimated ? '≈ stimata' : 'certa'}
        </span>
      )}
    </div>
  );
};

const PropertiesManager = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [sendingCredsFor, setSendingCredsFor] = useState(null);

  // ── Import massivo dal vecchio sito ──
  const [bulkImport, setBulkImport] = useState(null);
  const bulkTimerRef = React.useRef(null);
  const bulkRunningRef = React.useRef(false);
  const pollBulk = React.useCallback(async () => {
    clearTimeout(bulkTimerRef.current);
    try {
      const res = await axios.get(`${API}/properties/import-from-old-site/bulk/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('hw_token')}` }
      });
      const b = res.data.bulk;
      setBulkImport(prev => {
        if (prev?.status === 'running' && b && b.status !== 'running') {
          toast.success(`Import massivo ${b.status === 'cancelled' ? 'annullato' : 'completato'}: ${b.ok || 0} importati, ${b.failed || 0} falliti su ${b.done || 0}. Email di report inviata.`, { duration: 10000 });
        }
        return b;
      });
      bulkRunningRef.current = b?.status === 'running';
      if (bulkRunningRef.current) {
        bulkTimerRef.current = setTimeout(() => pollBulk(), 4000);
      }
    } catch {
      // errore di rete/tab in sospensione: NON fermare il polling, riprova più lentamente
      if (bulkRunningRef.current) {
        bulkTimerRef.current = setTimeout(() => pollBulk(), 8000);
      }
    }
  }, []);
  useEffect(() => {
    pollBulk();
    const onWake = () => { if (document.visibilityState !== 'hidden') pollBulk(); };
    window.addEventListener('focus', onWake);
    document.addEventListener('visibilitychange', onWake);
    return () => {
      clearTimeout(bulkTimerRef.current);
      window.removeEventListener('focus', onWake);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, [pollBulk]);

  const startBulkImport = async () => {
    if (!window.confirm("Importare dal vecchio sito TUTTI gli immobili (priorità agli attivi)?\n\nL'operazione è sequenziale (uno alla volta) e può durare ore: descrizione, servizi, regole, costi, FOTO e LISTINI verranno sovrascritti con i dati del vecchio sito. Gli immobili importati nelle ultime 24 ore vengono saltati.\n\nAl termine riceverai una email di report con gli immobili non importati.")) return;
    try {
      const res = await axios.post(`${API}/properties/import-from-old-site/bulk`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('hw_token')}` }
      });
      toast.info(`Import massivo avviato: ${res.data.total} immobili in coda${res.data.skipped_recent ? ` (${res.data.skipped_recent} saltati perché già importati di recente)` : ''}`, { duration: 8000 });
      pollBulk();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore avvio import massivo');
    }
  };

  const cancelBulkImport = async () => {
    try {
      await axios.post(`${API}/properties/import-from-old-site/bulk/cancel`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('hw_token')}` }
      });
      toast.info("Annullamento richiesto: si fermerà al termine dell'immobile corrente");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore annullamento');
    }
  };

  const handleRequestCredentials = async (property) => {
    setSendingCredsFor(property.id);
    try {
      const token = localStorage.getItem('hw_token');
      const res = await axios.post(`${API}/owner-credentials/send-for-property/${property.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.sent) {
        toast.success(`Richiesta credenziali inviata a ${res.data.to}`);
      } else {
        toast.warning(`Email non inviata a ${res.data?.to || 'destinatario'}. Riprova.`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || "Errore durante l'invio della richiesta");
      toast.error(msg);
    } finally {
      setSendingCredsFor(null);
    }
  };

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [completenessFilter, setCompletenessFilter] = useState('all'); // NEW: completeness filter
  const [managerFilter, setManagerFilter] = useState('all'); // Filter by manager association
  const [delegationFilter, setDelegationFilter] = useState('all'); // Filter by ROSS1000 delegation
  const [questuraFilter, setQuesturaFilter] = useState('all'); // Filter by Questura credentials
  const [showCredAudit, setShowCredAudit] = useState(false);
  const [credAuditLogs, setCredAuditLogs] = useState([]);

  const openCredAudit = async (propertyId) => {
    setShowCredAudit(true);
    setCredAuditLogs([]);
    try {
      const res = await axios.get(`${API}/credential-audit-log?property_id=${propertyId}&limit=100`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('hw_token')}` }
      });
      setCredAuditLogs(res.data.logs || []);
    } catch (e) {
      console.error('Errore caricamento storico credenziali:', e);
    }
  };
  const [regionFilter, setRegionFilter] = useState('all'); // NEW: Filter by region
  const [regionsWithProperties, setRegionsWithProperties] = useState([]); // Regions that have properties
  const [showNewProperty, setShowNewProperty] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  
  // Sorting states
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  
  const [newProperty, setNewProperty] = useState({
    property_code: '',
    name: '',
    address: '',
    country: 'Italia',
    region: '',
    province: '',
    city: '',
    postal_code: '',
    property_type: '',
    bedrooms: 1,
    bathrooms: 1,
    max_guests: 2,
    cleaning_cost: 0,
    linen_cost_per_person: 0,
    apply_submultiples: false,
    available_submultiples: [],
    partner_code: 'none',
    property_manager_id: 'none',
    police_station_code: '',
    tourism_observatory: '',
    booking_property_id: '',
    airbnb_property_id: '',
    holidayworld_url: '',
    cin: '',
    cir: '',
    intervention_budget: 0,
    last_minute_enabled: false,
    last_minute_discount: 10,
    owner_name: '',
    owner_email: '',
    owner_iban: '',
    owner_birth_date: '',
    // AlloggiatiWeb fields
    alloggiati_enabled: false,
    alloggiati_username: '',
    alloggiati_password: '',
    alloggiati_wskey: '',
    alloggiati_structure_code: '',
    alloggiati_apartment_id: '',
    alloggiati_structure_type: '',
    alloggiati_beds_count: '',
    owner_self_manages_questura_istat: false,
    owner_payment_method: 'bank_transfer',
    owner_paypal_email: '',
    segnalatore_id: 'none'
  });

  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [partners, setPartners] = useState([]);
  const [segnalatori, setSegnalatori] = useState([]);
  const [propertyManagers, setPropertyManagers] = useState([]);
  
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isAssociatingBookings, setIsAssociatingBookings] = useState(false);
  const [associationResult, setAssociationResult] = useState(null);
  const [showStatusManager, setShowStatusManager] = useState(false);
  const [statusAction, setStatusAction] = useState('close'); // 'close' or 'terminate'
  const [selectedPropertyForStatus, setSelectedPropertyForStatus] = useState('');
  const [closureData, setClosureData] = useState({
    startDate: '',
    endDate: '',
    closureType: 'property_initiated',
    reason: ''
  });
  const [terminationDate, setTerminationDate] = useState('');

  // Inline status change (dropdown nella colonna Stato)
  const [statusChange, setStatusChange] = useState({ open: false, property: null, target: null });
  const [statusChangeReason, setStatusChangeReason] = useState('');
  const [statusChangeDate, setStatusChangeDate] = useState('');
  const [statusChangeSubmitting, setStatusChangeSubmitting] = useState(false);

  // XML Generator state
  const [xmlDialogOpen, setXmlDialogOpen] = useState(false);
  const [xmlProperty, setXmlProperty] = useState(null);
  const [xmlDateRange, setXmlDateRange] = useState({
    start: new Date().toISOString().slice(0, 8) + '01',
    end: new Date().toISOString().slice(0, 10)
  });
  const [generatingXml, setGeneratingXml] = useState(false);

  // Duplicates state
  const [duplicatesDialogOpen, setDuplicatesDialogOpen] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [duplicatesStats, setDuplicatesStats] = useState({ totalGroups: 0, totalRecords: 0 });
  const [processingDuplicate, setProcessingDuplicate] = useState(null);

  // Archive state
  const [archivedDialogOpen, setArchivedDialogOpen] = useState(false);
  const [archivedProperties, setArchivedProperties] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archivingProperty, setArchivingProperty] = useState(null);

  // Suspended state
  const [suspendedDialogOpen, setSuspendedDialogOpen] = useState(false);
  const [suspendedProperties, setSuspendedProperties] = useState([]);
  const [suspendedLoading, setSuspendedLoading] = useState(false);

  // Owner Costs Budget History modal state
  const [showBudgetHistory, setShowBudgetHistory] = useState(null); // property_id
  const [budgetHistory, setBudgetHistory] = useState([]);
  const [budgetHistoryLoading, setBudgetHistoryLoading] = useState(false);
  const [showManagerHistory, setShowManagerHistory] = useState(null); // property_id
  const [managerHistory, setManagerHistory] = useState([]);
  const [managerHistoryLoading, setManagerHistoryLoading] = useState(false);

  // Password visibility states
  const [showPassword, setShowPassword] = useState({
    alloggiati_password: false,
    ross1000_password: false
  });
  // Reveal credenziali in chiaro (decifrate dal backend, ruoli ufficio)
  const [credsRevealed, setCredsRevealed] = useState(false);
  const [revealingCreds, setRevealingCreds] = useState(false);

  // ROSS1000 group credentials cache (loaded on mount, keyed by province code)
  const [ross1000GroupCreds, setRoss1000GroupCreds] = useState({}); // { "GE": {username, password, province, region}, ... }

  // Calendar dialog for bookings + closures
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [calendarProperty, setCalendarProperty] = useState(null);
  const [calendarData, setCalendarData] = useState({ bookings: [], closures: [] });
  const [calendarLoading, setCalendarLoading] = useState(false);

  // Bulk association state
  const [associationDialogOpen, setAssociationDialogOpen] = useState(false);
  const [associationStats, setAssociationStats] = useState(null);
  const [unassociatedBookings, setUnassociatedBookings] = useState([]);
  const [unassociatedClosures, setUnassociatedClosures] = useState([]);
  const [associationLoading, setAssociationLoading] = useState(false);
  const [bulkAssociating, setBulkAssociating] = useState(false);

  // Mappa completa regioni italiane -> codici provincia CIR
  const ITALY_REGIONS_CIR_MAP = {
    "PIEMONTE": { nome: "Piemonte", codes: ["001", "002", "003", "004", "005", "006", "096", "103"] },
    "VALLE_AOSTA": { nome: "Valle d'Aosta", codes: ["007"] },
    "LIGURIA": { nome: "Liguria", codes: ["008", "009", "010", "011"] },
    "LOMBARDIA": { nome: "Lombardia", codes: ["012", "013", "014", "015", "016", "017", "018", "019", "020", "097", "098", "108"] },
    "TRENTINO_ALTO_ADIGE": { nome: "Trentino-Alto Adige", codes: ["021", "022"] },
    "VENETO": { nome: "Veneto", codes: ["023", "024", "025", "026", "027", "028", "029"] },
    "FRIULI_VENEZIA_GIULIA": { nome: "Friuli-Venezia Giulia", codes: ["030", "031", "032", "093"] },
    "EMILIA_ROMAGNA": { nome: "Emilia-Romagna", codes: ["033", "034", "035", "036", "037", "038", "039", "040", "099"] },
    "TOSCANA": { nome: "Toscana", codes: ["045", "046", "047", "048", "049", "050", "051", "052", "053", "100"] },
    "UMBRIA": { nome: "Umbria", codes: ["054", "055"] },
    "MARCHE": { nome: "Marche", codes: ["041", "042", "043", "044", "109"] },
    "LAZIO": { nome: "Lazio", codes: ["056", "057", "058", "059", "060"] },
    "ABRUZZO": { nome: "Abruzzo", codes: ["066", "067", "068", "069"] },
    "MOLISE": { nome: "Molise", codes: ["070", "094"] },
    "CAMPANIA": { nome: "Campania", codes: ["061", "062", "063", "064", "065"] },
    "PUGLIA": { nome: "Puglia", codes: ["071", "072", "073", "074", "075", "110"] },
    "BASILICATA": { nome: "Basilicata", codes: ["076", "077"] },
    "CALABRIA": { nome: "Calabria", codes: ["078", "079", "080", "101", "102"] },
    "SICILIA": { nome: "Sicilia", codes: ["081", "082", "083", "084", "085", "086", "087", "088", "089"] },
    "SARDEGNA": { nome: "Sardegna", codes: ["090", "091", "092", "095", "104", "105", "106", "107", "111"] }
  };

  useEffect(() => {
    fetchProperties();
    fetchCountries();
    fetchPartners();
    fetchPropertyManagers();
    fetchRegionsWithProperties();
    fetchRoss1000GroupCredentials();
  }, []);

  // Re-fetch dropdown regioni quando cambia il filtro stato (Tutti/Attivi/Cessati/Archiviati)
  useEffect(() => {
    fetchRegionsWithProperties(statusFilter);
  }, [statusFilter]);

  // Fetch all ROSS1000 group credentials once — used for auto-populating username/password by province
  const fetchRoss1000GroupCredentials = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const res = await axios.get(`${API}/ross1000/group-credentials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const map = {};
      (res.data?.credentials || []).forEach(c => {
        if (c.province_code) map[c.province_code] = c;
      });
      setRoss1000GroupCreds(map);
    } catch (err) {
      console.warn('Impossibile caricare credenziali di gruppo ROSS1000', err);
    }
  };

  // Quick-create dialog per Account di Gruppo ROSS1000 (apribile direttamente dalla scheda immobile)
  const [quickGroupDialog, setQuickGroupDialog] = useState({ open: false, propertyContext: null });
  const [quickGroupForm, setQuickGroupForm] = useState({ region: '', province: '', province_code: '', username: '', password: '' });
  const [quickGroupSaving, setQuickGroupSaving] = useState(false);

  const openQuickGroupDialog = () => {
    // Pre-popola con i dati attuali della scheda immobile (regione + provincia)
    const pCode = provinceToCode(newProperty.province) || '';
    setQuickGroupForm({
      region: newProperty.region || '',
      province: newProperty.province || '',
      province_code: pCode || (newProperty.province && newProperty.province.length === 2 ? newProperty.province.toUpperCase() : ''),
      username: '',
      password: '',
    });
    setQuickGroupDialog({ open: true, propertyContext: { region: newProperty.region, province: newProperty.province } });
  };

  const submitQuickGroupCred = async () => {
    const { region, province, province_code, username, password } = quickGroupForm;
    if (!region || !province || !province_code) {
      toast.error('Compila Regione, Provincia e Codice Provincia');
      return;
    }
    if (province_code.trim().length !== 2) {
      toast.error('Codice provincia: 2 lettere (es. TA, GE)');
      return;
    }
    if (!username || !password) {
      toast.error('Username e Password ROSS1000 sono obbligatori');
      return;
    }
    setQuickGroupSaving(true);
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(
        `${API}/ross1000/group-credentials`,
        {
          region: region.trim(),
          province: province.trim(),
          province_code: province_code.trim().toUpperCase(),
          username: username.trim(),
          password,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Account di gruppo creato per ${province} (${province_code.toUpperCase()})`);
      // Ricarica le credenziali e auto-imposta mode=group
      await fetchRoss1000GroupCredentials();
      setNewProperty(prev => ({ ...prev, ross1000_credentials_mode: 'group' }));
      setQuickGroupDialog({ open: false, propertyContext: null });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403) {
        toast.error('Non autorizzato: solo admin e customer service possono creare account di gruppo');
      } else if (err.response?.status === 409) {
        toast.error(detail || 'Account già esistente per questa provincia');
      } else {
        toast.error(detail || 'Errore creazione account di gruppo');
      }
    } finally {
      setQuickGroupSaving(false);
    }
  };

  // Normalize province (either full name or 2-letter code) → 2-letter code
  const provinceToCode = (provinceRaw) => {
    if (!provinceRaw) return null;
    const key = String(provinceRaw).trim().toLowerCase();
    const map = {
      'genova': 'GE', 'ge': 'GE',
      'imperia': 'IM', 'im': 'IM',
      'la spezia': 'SP', 'spezia': 'SP', 'sp': 'SP',
      'savona': 'SV', 'sv': 'SV'
    };
    if (map[key]) return map[key];
    const upper = String(provinceRaw).trim().toUpperCase();
    if (upper.length === 2) return upper;
    return null;
  };

  // Returns the group credentials matching the property's current province, or null
  const getGroupCredsForProvince = (provinceRaw) => {
    const code = provinceToCode(provinceRaw);
    if (!code) return null;
    return ross1000GroupCreds[code] || null;
  };

  // Check for edit query param
  useEffect(() => {
    const editPropertyId = searchParams.get('edit');
    if (editPropertyId && properties.length > 0) {
      const propertyToEdit = properties.find(p => p.id === editPropertyId);
      if (propertyToEdit) {
        handleEditProperty(propertyToEdit);
        // Remove query param after opening
        setSearchParams({});
      }
    }
  }, [searchParams, properties]);

  useEffect(() => {
    if (newProperty.country) {
      fetchRegions(newProperty.country);
    }
  }, [newProperty.country]);

  useEffect(() => {
    if (newProperty.region) {
      fetchCities(newProperty.region);
    }
  }, [newProperty.region]);

  // Load Owner Costs Budget history when modal opens
  useEffect(() => {
    if (!showBudgetHistory) {
      setBudgetHistory([]);
      return;
    }
    const fetchHistory = async () => {
      setBudgetHistoryLoading(true);
      try {
        const token = localStorage.getItem('hw_token');
        const res = await axios.get(`${API}/owner-costs/budget-history/${showBudgetHistory}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBudgetHistory(res.data.history || []);
      } catch (e) {
        console.error('Error fetching budget history:', e);
        toast.error('Errore caricamento storico modifiche');
      } finally {
        setBudgetHistoryLoading(false);
      }
    };
    fetchHistory();
  }, [showBudgetHistory]);

  // Load Responsabili (manager) history when modal opens
  useEffect(() => {
    if (!showManagerHistory) {
      setManagerHistory([]);
      return;
    }
    const fetchManagerHistory = async () => {
      setManagerHistoryLoading(true);
      try {
        const token = localStorage.getItem('hw_token');
        const res = await axios.get(`${API}/properties/${showManagerHistory}/manager-history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setManagerHistory(res.data.history || []);
      } catch (e) {
        console.error('Error fetching manager history:', e);
        toast.error('Errore caricamento storico responsabili');
      } finally {
        setManagerHistoryLoading(false);
      }
    };
    fetchManagerHistory();
  }, [showManagerHistory]);

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/properties?include_archived=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRegionsWithProperties = async (statusOverride) => {
    try {
      const token = localStorage.getItem('hw_token');
      const status = statusOverride !== undefined ? statusOverride : statusFilter;
      const response = await axios.get(
        `${API}/tourism/regions-with-properties?status=${encodeURIComponent(status || 'all')}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRegionsWithProperties(response.data.regions || []);
    } catch (error) {
      console.error('Error fetching regions with properties:', error);
    }
  };

  // Fetch duplicates
  const fetchDuplicates = async () => {
    setDuplicatesLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/admin/properties/duplicates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDuplicateGroups(response.data.duplicate_groups || []);
      setDuplicatesStats({
        totalGroups: response.data.total_duplicate_groups || 0,
        totalRecords: response.data.total_duplicate_records || 0
      });
    } catch (error) {
      console.error('Error fetching duplicates:', error);
      alert('Errore nel caricamento dei duplicati');
    } finally {
      setDuplicatesLoading(false);
    }
  };

  // Merge duplicates
  const handleMergeDuplicates = async (targetId, sourceIds, targetName) => {
    if (!window.confirm(`Unire ${sourceIds.length} record a "${targetName}"?\n\nI dati mancanti verranno importati e i duplicati eliminati.`)) {
      return;
    }
    setProcessingDuplicate(targetId);
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(
        `${API}/admin/properties/merge-duplicates/${targetId}`,
        sourceIds,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(response.data.message);
      await fetchDuplicates();
      await fetchProperties();
    } catch (error) {
      console.error('Error merging duplicates:', error);
      alert(error.response?.data?.detail || 'Errore durante l\'unione');
    } finally {
      setProcessingDuplicate(null);
    }
  };

  // Open duplicates dialog
  const openDuplicatesDialog = () => {
    setDuplicatesDialogOpen(true);
    fetchDuplicates();
  };

  const fetchCountries = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/locations/countries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCountries(response.data.countries || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchRegions = async (country) => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/locations/regions?country=${encodeURIComponent(country)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegions(response.data.regions || []);
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const fetchCities = async (region) => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/locations/cities?region=${encodeURIComponent(region)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCities(response.data.cities || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  // Persist custom location to backend for future reuse
  const persistCustomLocation = async (type, name, parent = '') => {
    if (!name) return;
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(`${API}/locations/custom`, { type, name, parent }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
    } catch (error) {
      // Silent - the value is still used in the form even if persistence fails
    }
  };

  const handleCountryChange = (value) => {
    if (!countries.includes(value)) persistCustomLocation('country', value);
    setNewProperty({...newProperty, country: value, region: '', province: '', city: ''});
  };

  const handleRegionChange = (value) => {
    if (!regions.includes(value)) persistCustomLocation('region', value, newProperty.country);
    setNewProperty({...newProperty, region: value, province: '', city: ''});
  };

  const handleCityChange = (value) => {
    if (!cities.includes(value)) persistCustomLocation('city', value, newProperty.region);
    setNewProperty({...newProperty, city: value});
  };

  const fetchPartners = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  const fetchSegnalatori = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/properties/segnalatori`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSegnalatori(response.data?.segnalatori || []);
    } catch (error) {
      // HW Academy non raggiungibile / non configurata: campo opzionale, non bloccante
      console.warn('Error fetching segnalatori (HW Academy):', error?.message);
      setSegnalatori([]);
    }
  };

  // Filtra i segnalatori in base al partner selezionato (chiave comune: partner_code).
  // Se nessun partner è selezionato, mostra tutti i segnalatori.
  const getSegnalatoriForPartner = (partnerCode) => {
    if (!partnerCode || partnerCode === 'none') return segnalatori;
    return segnalatori.filter(s => String(s.partner_code || '') === String(partnerCode));
  };

  // Associazione inversa: scegliendo il segnalatore, auto-seleziona il partner
  // corrispondente (dal partner_code allegato al segnalatore da HW Academy).
  const handleSegnalatoreChange = (value) => {
    if (!value || value === 'none') {
      setNewProperty(prev => ({ ...prev, segnalatore_id: value }));
      return;
    }
    const seg = segnalatori.find(s => s.segnalatore_id === value);
    setNewProperty(prev => ({
      ...prev,
      segnalatore_id: value,
      partner_code: (seg && seg.partner_code) ? String(seg.partner_code) : prev.partner_code,
    }));
  };

  // Archive functions
  const fetchArchivedProperties = async () => {
    setArchivedLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/properties/archived`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArchivedProperties(response.data);
    } catch (error) {
      console.error('Error fetching archived properties:', error);
    } finally {
      setArchivedLoading(false);
    }
  };

  const handleRestoreProperty = async (property) => {
    if (!window.confirm(`Ripristinare "${property.name}"?\n\nL'immobile tornerà visibile e attivo in tutti i processi.`)) return;

    setArchivingProperty(property.id);
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(`${API}/properties/${property.id}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Immobile "${property.name}" ripristinato con successo`);
      await fetchArchivedProperties();
      await fetchProperties();
    } catch (error) {
      console.error('Error restoring property:', error);
      alert(error.response?.data?.detail || 'Errore durante il ripristino');
    } finally {
      setArchivingProperty(null);
    }
  };

  const openArchivedDialog = () => {
    setArchivedDialogOpen(true);
    fetchArchivedProperties();
  };

  // Suspended properties functions
  const fetchSuspendedProperties = async () => {
    setSuspendedLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/properties/suspended`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuspendedProperties(response.data);
    } catch (error) {
      console.error('Error fetching suspended properties:', error);
    } finally {
      setSuspendedLoading(false);
    }
  };

  const handleSuspendProperty = async (property) => {
    const reason = window.prompt(`Motivo sospensione per "${property.name}":\n(es: Lavori in corso, Problema temporaneo, etc.)`);
    if (reason === null) return; // User cancelled

    setArchivingProperty(property.id);
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(`${API}/properties/${property.id}/suspend?reason=${encodeURIComponent(reason || 'Sospeso manualmente')}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Immobile "${property.name}" sospeso con successo`);
      await fetchProperties();
    } catch (error) {
      console.error('Error suspending property:', error);
      alert(error.response?.data?.detail || 'Errore durante la sospensione');
    } finally {
      setArchivingProperty(null);
    }
  };

  const handleActivateProperty = async (property) => {
    if (!window.confirm(`Riattivare "${property.name}"?\n\nL'immobile tornerà visibile e attivo in tutti i processi.`)) return;

    setArchivingProperty(property.id);
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(`${API}/properties/${property.id}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Immobile "${property.name}" attivato con successo`);
      await fetchSuspendedProperties();
      await fetchArchivedProperties();
      await fetchProperties();
    } catch (error) {
      console.error('Error activating property:', error);
      alert(error.response?.data?.detail || 'Errore durante l\'attivazione');
    } finally {
      setArchivingProperty(null);
    }
  };

  const openSuspendedDialog = () => {
    setSuspendedDialogOpen(true);
    fetchSuspendedProperties();
  };

  // Bulk Association functions
  const openAssociationDialog = async () => {
    setAssociationDialogOpen(true);
    setAssociationLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      
      // Fetch all data in parallel
      const [statsRes, bookingsRes, closuresRes] = await Promise.all([
        axios.get(`${API}/association-stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/bookings/unassociated`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/closures/unassociated`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setAssociationStats(statsRes.data);
      setUnassociatedBookings(bookingsRes.data.bookings || []);
      setUnassociatedClosures(closuresRes.data.closures || []);
    } catch (error) {
      console.error('Error fetching association data:', error);
    } finally {
      setAssociationLoading(false);
    }
  };

  const handleBulkAssociate = async () => {
    if (!window.confirm('Vuoi associare automaticamente tutte le prenotazioni e chiusure agli immobili corrispondenti?\n\nQuesta operazione può richiedere alcuni secondi.')) {
      return;
    }
    
    setBulkAssociating(true);
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(`${API}/bulk-associate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const result = response.data;
      alert(`Associazione completata!\n\n✅ Prenotazioni associate: ${result.total_bookings_associated}\n✅ Chiusure associate: ${result.total_closures_associated}\n📦 Immobili processati: ${result.properties_processed}`);
      
      // Refresh data
      await openAssociationDialog();
    } catch (error) {
      console.error('Error in bulk association:', error);
      alert('Errore durante l\'associazione bulk');
    } finally {
      setBulkAssociating(false);
    }
  };

  const fetchPropertyManagers = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/property-managers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPropertyManagers(response.data);
    } catch (error) {
      console.error('Error fetching property managers:', error);
    }
  };


  const handleCreateProperty = async (e) => {
    e.preventDefault();
    
    // Validazione frontend
    if (!newProperty.property_code?.trim()) {
      alert('Il codice immobile è obbligatorio');
      return;
    }
    if (!newProperty.name?.trim()) {
      alert('Il nome dell\'immobile è obbligatorio');
      return;
    }
    
    try {
      const token = localStorage.getItem('hw_token');
      const payload = {
        ...newProperty,
        bedrooms: parseInt(newProperty.bedrooms) || 1,
        bathrooms: parseInt(newProperty.bathrooms) || 1,
        max_guests: parseInt(newProperty.max_guests) || 2,
        cleaning_cost: parseFloat(newProperty.cleaning_cost) || 0,
        linen_cost_per_person: parseFloat(newProperty.linen_cost_per_person) || 0,
        alloggiati_beds_count: newProperty.alloggiati_beds_count === '' ? null : parseInt(newProperty.alloggiati_beds_count) || null,
        intervention_budget: newProperty.intervention_budget === '' || newProperty.intervention_budget === 0 ? null : parseFloat(newProperty.intervention_budget) || null,
        last_minute_discount: parseFloat(newProperty.last_minute_discount) || 10,
        segnalatore_id: newProperty.segnalatore_id === 'none' ? null : (newProperty.segnalatore_id || null),
      };
      const response = await axios.post(`${API}/properties`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowNewProperty(false);
      const createdProperty = response.data;
      
      // Reset form
      setNewProperty({
        property_code: '',
        name: '',
        address: '',
        country: 'Italia',
        region: '',
        province: '',
        city: '',
        postal_code: '',
        property_type: '',
        bedrooms: 1,
        bathrooms: 1,
        max_guests: 2,
        cleaning_cost: 0,
        partner_code: 'none',
        property_manager_id: 'none',
        police_station_code: '',
        tourism_observatory: '',
        booking_property_id: '',
        airbnb_property_id: '',
        holidayworld_url: ''
      });
      
      fetchProperties();
      alert(`Immobile "${createdProperty.name}" creato con successo! ID: ${createdProperty.id}`);
      
      // Ask user if they want to auto-associate bookings
      if (createdProperty.property_code) {
        const shouldAssociate = window.confirm(
          `Vuoi associare automaticamente tutte le prenotazioni che iniziano con "${createdProperty.property_code}"?`
        );
        if (shouldAssociate) {
          await handleAutoAssociateBookings(createdProperty.id, createdProperty.name);
        }
      }
    } catch (error) {
      console.error('Error creating property:', error);
      const msg = error.response?.data?.detail || error.message || 'Errore sconosciuto';
      alert(`Errore durante la creazione dell'immobile:\n${msg}`);
    }
  };

  const handleAutoAssociateBookings = async (propertyId, propertyName) => {
    setIsAssociatingBookings(true);
    setAssociationResult(null);
    
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(`${API}/properties/${propertyId}/associate-bookings`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const result = response.data;
      setAssociationResult(result);
      
      // Show success message
      alert(`🎉 Associazione completata per "${propertyName}"!\n\n` +
            `📊 Prenotazioni trovate: ${result.bookings_found}\n` +
            `✅ Prenotazioni associate: ${result.bookings_updated}\n` +
            `ℹ️ Già associate: ${result.bookings_already_associated}`);
      
    } catch (error) {
      console.error('Error auto-associating bookings:', error);
      alert('Errore durante l\'associazione automatica delle prenotazioni');
    } finally {
      setIsAssociatingBookings(false);
    }
  };

  // Fetch calendar data (bookings + closures) for a property
  const handleOpenCalendar = async (property) => {
    setCalendarProperty(property);
    setCalendarDialogOpen(true);
    setCalendarLoading(true);
    
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/properties/${property.id}/calendar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarData(response.data);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      setCalendarData({ bookings: [], closures: [] });
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleCreateClosure = async () => {
    if (!selectedPropertyForStatus || !closureData.startDate || !closureData.endDate) {
      alert('Compila tutti i campi richiesti');
      return;
    }

    try {
      const token = localStorage.getItem('hw_token');
      const property = properties.find(p => p.id === selectedPropertyForStatus);
      
      const response = await axios.post(`${API}/closures`, {
        property_id: selectedPropertyForStatus,
        start_date: new Date(closureData.startDate).toISOString(),
        end_date: new Date(closureData.endDate).toISOString(),
        closure_type: closureData.closureType,
        reason: closureData.reason || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const result = response.data;
      alert(`✅ Periodo di chiusura creato per "${property?.name}"!\n\n` +
            `📅 Dal: ${new Date(closureData.startDate).toLocaleDateString()}\n` +
            `📅 Al: ${new Date(closureData.endDate).toLocaleDateString()}\n` +
            `🏢 Tipo: ${closureData.closureType === 'property_initiated' ? 'Proprietà' : 'Ufficio'}\n\n` +
            `ℹ️ L'immobile rimane ATTIVO con periodo di chiusura temporanea`);

      setShowStatusManager(false);
      setSelectedPropertyForStatus('');
      setClosureData({ startDate: '', endDate: '', closureType: 'property_initiated', reason: '' });
    } catch (error) {
      console.error('Error creating closure:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante la creazione del periodo di chiusura';
      alert(errorMsg);
    }
  };

  const handleTerminateProperty = async () => {
    if (!selectedPropertyForStatus || !terminationDate) {
      alert('Compila tutti i campi richiesti');
      return;
    }

    try {
      const token = localStorage.getItem('hw_token');
      const property = properties.find(p => p.id === selectedPropertyForStatus);
      
      const response = await axios.post(`${API}/properties/terminate`, {
        property_id: selectedPropertyForStatus,
        termination_date: new Date(terminationDate).toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const result = response.data;
      alert(`✅ Immobile "${property?.name}" cessato con successo!\n\n` +
            `📅 Data cessazione: ${new Date(terminationDate).toLocaleDateString()}`);

      setShowStatusManager(false);
      setSelectedPropertyForStatus('');
      setTerminationDate('');
      fetchProperties();
    } catch (error) {
      console.error('Error terminating property:', error);
      alert('Errore durante la cessazione dell\'immobile');
    }
  };

  const handleReactivateProperty = async (propertyId, propertyName) => {
    if (!window.confirm(`Vuoi riattivare l'immobile "${propertyName}"?`)) return;

    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(`${API}/properties/${propertyId}/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(`✅ Immobile "${propertyName}" riattivato con successo!`);
      fetchProperties();
    } catch (error) {
      console.error('Error reactivating property:', error);
      alert('Errore durante la riattivazione dell\'immobile');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Attivo', className: 'bg-emerald-100 text-emerald-800' },
      private: { label: 'Private', className: 'bg-amber-100 text-amber-800' },
      terminated: { label: 'Cessato', className: 'bg-gray-100 text-gray-800' }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  // --- Gestione stato inline (tendina nella colonna Stato) ---
  const STATUS_META = {
    active: { label: 'Attivo', trigger: 'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200' },
    suspended: { label: 'Sospeso', trigger: 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200' },
    terminated: { label: 'Cessato', trigger: 'bg-gray-200 text-gray-700 border-gray-300 hover:bg-gray-300' },
    archived: { label: 'Archiviato', trigger: 'bg-slate-200 text-slate-700 border-slate-300 hover:bg-slate-300' },
    private: { label: 'Private', trigger: 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200' },
  };

  const getEffectiveStatus = (property) => {
    if (property?.archived === true) return 'archived';
    if (property?.suspended === true) return 'suspended';
    if (property?.status === 'terminated') return 'terminated';
    if (property?.status === 'private') return 'private';
    return 'active';
  };

  // True se l'immobile è ATTIVO ma ha una cessazione programmata in data futura
  const isScheduledTermination = (property) => (
    property?.termination_scheduled === true &&
    !property?.archived && !property?.suspended &&
    property?.status !== 'terminated'
  );

  const formatItDate = (val) => {
    if (!val) return '';
    try { return new Date(val).toLocaleDateString('it-IT'); } catch { return ''; }
  };

  // Apre il dialog di conferma quando si seleziona un nuovo stato dalla tendina
  const onSelectStatus = (property, target) => {
    if (!property || !target) return;
    // 'cancel_schedule' è ammesso anche se lo stato effettivo è già 'active' (cessazione programmata)
    if (target !== 'cancel_schedule' && getEffectiveStatus(property) === target) return; // nessun cambiamento
    setStatusChangeReason('');
    setStatusChangeDate('');
    setStatusChange({ open: true, property, target });
  };

  const closeStatusChange = () => {
    if (statusChangeSubmitting) return;
    setStatusChange({ open: false, property: null, target: null });
    setStatusChangeReason('');
    setStatusChangeDate('');
  };

  // Esegue il cambio stato chiamando l'endpoint corretto
  const submitStatusChange = async () => {
    const { property, target } = statusChange;
    if (!property || !target) return;

    if (target === 'terminated' && !statusChangeDate) {
      toast.error('Inserisci la data di cessazione');
      return;
    }

    setStatusChangeSubmitting(true);
    try {
      const token = localStorage.getItem('hw_token');
      const headers = { Authorization: `Bearer ${token}` };
      const current = getEffectiveStatus(property);

      if (target === 'cancel_schedule') {
        // Annulla una cessazione programmata (rimuove data e flag, resta Attivo)
        await axios.post(`${API}/properties/${property.id}/reactivate`, {}, { headers });
        toast.success(`Cessazione programmata annullata per "${property.name}"`);
      } else if (target === 'active') {
        if (current === 'terminated') {
          await axios.post(`${API}/properties/${property.id}/reactivate`, {}, { headers });
        } else {
          // sospeso o archiviato → /activate (rimuove entrambi i flag)
          await axios.post(`${API}/properties/${property.id}/activate`, {}, { headers });
        }
        toast.success(`"${property.name}" riattivato (Attivo)`);
      } else if (target === 'suspended') {
        const reason = statusChangeReason.trim() || 'Sospeso manualmente';
        await axios.post(`${API}/properties/${property.id}/suspend?reason=${encodeURIComponent(reason)}`, {}, { headers });
        toast.success(`"${property.name}" sospeso`);
      } else if (target === 'archived') {
        const reason = statusChangeReason.trim() || 'Archiviato manualmente';
        await axios.post(`${API}/properties/${property.id}/archive?reason=${encodeURIComponent(reason)}`, {}, { headers });
        toast.success(`"${property.name}" archiviato`);
      } else if (target === 'terminated') {
        const resp = await axios.post(`${API}/properties/terminate`, {
          property_id: property.id,
          termination_date: new Date(statusChangeDate).toISOString()
        }, { headers });
        const d = new Date(statusChangeDate).toLocaleDateString('it-IT');
        if (resp.data?.scheduled) {
          toast.success(`Cessazione programmata per "${property.name}" il ${d}. Resta Attivo fino a quella data.`);
        } else {
          toast.success(`"${property.name}" cessato dal ${d}`);
        }
      }

      setStatusChange({ open: false, property: null, target: null });
      setStatusChangeReason('');
      setStatusChangeDate('');
      await fetchProperties();
    } catch (error) {
      console.error('Error changing property status:', error);
      toast.error(error.response?.data?.detail || 'Errore durante il cambio di stato');
    } finally {
      setStatusChangeSubmitting(false);
    }
  };


  const handleEditProperty = (property) => {
    setSelectedProperty(property);
    setNewProperty({
      property_code: property.property_code,
      name: property.name,
      address: property.address || '',
      country: property.country || 'Italia',
      region: property.region || '',
      province: property.province || '',
      city: property.city || '',
      postal_code: property.postal_code || '',
      property_type: property.property_type || 'apartment',
      bedrooms: property.bedrooms || 1,
      bathrooms: property.bathrooms || 1,
      max_guests: property.max_guests || 2,
      cleaning_cost: property.cleaning_cost || 0,
      linen_cost_per_person: property.linen_cost_per_person || 0,
      apply_submultiples: property.apply_submultiples || false,
      available_submultiples: property.available_submultiples || [],
      partner_code: property.partner_code || 'none',
      property_manager_id: property.property_manager_id || 'none',
      police_station_code: property.police_station_code || '',
      tourism_observatory: property.tourism_observatory || '',
      booking_property_id: property.booking_property_id || '',
      airbnb_property_id: property.airbnb_property_id || '',
      holidayworld_url: property.holidayworld_url || '',
      cin: property.cin || '',
      cir: property.cir || '',
      intervention_budget: property.intervention_budget || 0,
      last_minute_enabled: property.last_minute_enabled || false,
      last_minute_discount: property.last_minute_discount || 10,
      owner_name: property.owner_name || '',
      owner_email: property.owner_email || '',
      owner_iban: property.owner_iban || '',
      owner_birth_date: property.owner_birth_date || '',
      segnalatore_id: property.segnalatore_id || 'none',
      alloggiati_enabled: property.alloggiati_enabled || false,
      alloggiati_username: property.alloggiati_username || '',
      alloggiati_password: property.alloggiati_password || '',
      alloggiati_wskey: property.alloggiati_wskey || '',
      alloggiati_structure_code: property.alloggiati_structure_code || '',
      alloggiati_apartment_id: property.alloggiati_apartment_id || '',
      alloggiati_structure_type: property.alloggiati_structure_type || '',
      alloggiati_beds_count: property.alloggiati_beds_count || '',
      // ROSS1000 / ISTAT regional credentials
      has_ross1000_delegation: property.has_ross1000_delegation || false,
      ross1000_username: property.ross1000_username || '',
      ross1000_password: property.ross1000_password || '',
      ross1000_structure_code: property.ross1000_structure_code || '',
      ross1000_cir_from_delegation: property.ross1000_cir_from_delegation || property.cir || '',
      ross1000_credentials_mode: property.ross1000_credentials_mode || null,
      istat_comune_code: property.istat_comune_code || '',
      owner_self_manages_questura_istat: property.owner_self_manages_questura_istat || false,
      owner_payment_method: property.owner_payment_method || 'bank_transfer',
      owner_paypal_email: property.owner_paypal_email || '',
      owner_annual_costs_budget: property.owner_annual_costs_budget ?? null,
      owner_daily_cost_incidence: property.owner_daily_cost_incidence ?? null,
      mid_term: !!(property.publication && property.publication.mid_term),
      mid_term_note: (property.publication && property.publication.mid_term_note) || '',
      _mid_term_loaded: false,
    });
    setCredsRevealed(false);
    setShowPassword({ alloggiati_password: false, ross1000_password: false });
    setShowEditDialog(true);
    hydrateCredentialsSilently(property.id);
    hydrateMidTerm(property.id);
  };

  // Carica lo stato Medio Termine dalla scheda pubblicazione (fonte di verità: publication.mid_term)
  const hydrateMidTerm = async (propertyId) => {
    if (!propertyId) return;
    try {
      const token = localStorage.getItem('hw_token');
      const res = await axios.get(`${API}/properties/${propertyId}/publication`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pubData = res.data?.publication || {};
      setNewProperty(prev => ({
        ...prev,
        mid_term: !!pubData.mid_term,
        mid_term_note: pubData.mid_term_note || '',
        _mid_term_loaded: true
      }));
    } catch (e) {
      console.error('Errore caricamento medio termine:', e);
    }
  };

  // Idratazione silenziosa anti-svuotamento: recupera sotto traccia le credenziali
  // reali (senza mostrarle: i campi restano mascherati) così il salvataggio della
  // scheda le rimanda intatte anche se la lista non le conteneva.
  const hydrateCredentialsSilently = async (propertyId) => {
    if (!propertyId) return;
    try {
      const token = localStorage.getItem('hw_token');
      const res = await axios.get(`${API}/properties/${propertyId}/credentials/reveal?silent=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data || {};
      setNewProperty(prev => ({
        ...prev,
        alloggiati_username: prev.alloggiati_username || d.alloggiati_username || '',
        alloggiati_password: d.alloggiati_password || prev.alloggiati_password || '',
        alloggiati_wskey: d.alloggiati_wskey || prev.alloggiati_wskey || '',
        ross1000_username: prev.ross1000_username || d.ross1000_username || '',
        ross1000_password: d.ross1000_password || prev.ross1000_password || '',
      }));
    } catch (_) {
      // ruoli senza permesso reveal (403) o errori di rete: la protezione backend copre comunque
    }
  };

  // Mostra le credenziali Questura/ROSS1000 in chiaro (decifrate lato backend).
  // Riempie i campi con i valori reali così l'ufficio può verificarli; al salvataggio
  // verranno ri-cifrati automaticamente (encrypt idempotente).
  const handleRevealCreds = async () => {
    if (credsRevealed) {
      // toggle visibilità soltanto
      setShowPassword(prev => ({ ...prev, alloggiati_password: !prev.alloggiati_password, ross1000_password: !prev.ross1000_password }));
      return;
    }
    if (!selectedProperty?.id) return;
    setRevealingCreds(true);
    try {
      const token = localStorage.getItem('hw_token');
      const res = await axios.get(`${API}/properties/${selectedProperty.id}/credentials/reveal`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data || {};
      setNewProperty(prev => ({
        ...prev,
        alloggiati_password: d.alloggiati_password || '',
        alloggiati_wskey: d.alloggiati_wskey || '',
        ross1000_password: d.ross1000_password || prev.ross1000_password || '',
      }));
      setCredsRevealed(true);
      setShowPassword({ alloggiati_password: true, ross1000_password: true });
      toast.success('Credenziali decifrate e mostrate in chiaro');
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('Non autorizzato a visualizzare le credenziali in chiaro');
      } else {
        toast.error('Impossibile decifrare le credenziali');
      }
    } finally {
      setRevealingCreds(false);
    }
  };

  const handleViewDetails = (property) => {
    setSelectedProperty(property);
    setShowDetailsDialog(true);
  };

  // Generate ROSS1000 XML for a single property
  const handleGenerateXml = (property) => {
    setXmlProperty(property);
    // Set default date range to current month
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const lastDay = new Date(year, month, 0).getDate();
    setXmlDateRange({
      start: `${year}-${String(month).padStart(2, '0')}-01`,
      end: `${year}-${String(month).padStart(2, '0')}-${lastDay}`
    });
    setXmlDialogOpen(true);
  };

  const downloadXml = async () => {
    if (!xmlProperty) return;
    
    try {
      setGeneratingXml(true);
      const token = localStorage.getItem('hw_token');
      
      // Determine province from CIR
      const cir = xmlProperty.cir || '';
      const provinceCode = cir.substring(0, 3);
      const provinceMap = {
        '008': 'Imperia',
        '009': 'Savona',
        '010': 'Genova',
        '011': 'La Spezia'
      };
      const province = provinceMap[provinceCode] || 'Genova';
      
      const response = await axios.post(
        `${API}/tourism/generate-ross1000-xml`,
        {
          province: province,
          data_inizio: xmlDateRange.start,
          data_fine: xmlDateRange.end,
          property_code: xmlProperty.property_code
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ROSS1000_${xmlProperty.property_code}_${xmlDateRange.start}_${xmlDateRange.end}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      alert(`File XML generato per ${xmlProperty.name}`);
      setXmlDialogOpen(false);
    } catch (err) {
      console.error('Error generating XML:', err);
      alert(err.response?.data?.detail || 'Errore nella generazione XML');
    } finally {
      setGeneratingXml(false);
    }
  };

  // Toggle ROSS1000 delegation for a property
  const handleToggleDelegation = async (property) => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(
        `${API}/tourism/toggle-ross1000-delegation/${property.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setProperties(prevProperties => 
        prevProperties.map(p => 
          p.id === property.id 
            ? { ...p, has_ross1000_delegation: response.data.has_ross1000_delegation }
            : p
        )
      );
      
      alert(`Delega ROSS1000 ${response.data.has_ross1000_delegation ? 'attivata' : 'disattivata'} per ${property.name}`);
    } catch (err) {
      console.error('Error toggling delegation:', err);
      alert(err.response?.data?.detail || 'Errore nel modificare la delega');
    }
  };

  // Helper function to get region from CIR code
  const getRegionFromCir = (cir) => {
    if (!cir || typeof cir !== 'string' || cir.length < 3) return null;
    const prefix = cir.substring(0, 3);
    
    for (const [regionKey, regionData] of Object.entries(ITALY_REGIONS_CIR_MAP)) {
      if (regionData.codes.includes(prefix)) {
        return { key: regionKey, name: regionData.nome };
      }
    }
    return null;
  };

  // Helper function to check if property is in a region that supports delegations
  // Now supports ALL Italian regions, not just Liguria
  const isRegionWithDelegation = (property) => {
    const cir = property.cir || '';
    if (!cir || cir.length < 3) return false;
    const region = getRegionFromCir(cir);
    return region !== null;
  };

  // Legacy function - kept for backward compatibility
  const isLiguriaProperty = (property) => {
    const cir = property.cir || '';
    return cir.startsWith('008') || cir.startsWith('009') || cir.startsWith('010') || cir.startsWith('011');
  };

  // Sorting function
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Helper function to check if property has a VALID manager (exists in propertyManagers OR is self-managed)
  const hasValidManager = (property) => {
    if (!property.property_manager_id) return false;
    // "self" means the property is self-managed by the owner
    if (property.property_manager_id === 'self') return true;
    return propertyManagers.some(m => m.id === property.property_manager_id);
  };

  // Count properties without valid manager (no manager OR orphan manager_id)
  const propertiesWithoutValidManager = properties.filter(p => !hasValidManager(p)).length;

  // Filter, sort and paginate properties
  const filteredProperties = properties.filter(property => {
    const matchesSearch = (property.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (property.city || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (property.address || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (property.property_code && property.property_code.toLowerCase().includes(searchTerm.toLowerCase()));
    // "Tutti gli stati": durante una ricerca testuale include ANCHE gli archiviati
    // (l'utente che cerca per nome vuole trovarli ovunque); in navigazione senza
    // ricerca, gli archiviati restano nascosti per non intasare la lista.
    const hasSearch = (searchTerm || '').trim().length > 0;
    const matchesStatus = statusFilter === 'archived' ? property.archived === true : 
                         (statusFilter === 'all' ? (hasSearch ? true : !property.archived) : 
                          (property.status === statusFilter && !property.archived));
    const matchesCompleteness = completenessFilter === 'all' || 
                               (completenessFilter === 'complete' && !property.is_incomplete) ||
                               (completenessFilter === 'incomplete' && property.is_incomplete);
    // Updated: check if manager actually exists, not just if manager_id is set
    const matchesManager = managerFilter === 'all' || 
                          (managerFilter === 'with' && hasValidManager(property)) ||
                          (managerFilter === 'without' && !hasValidManager(property));
    // ISTAT/ROSS1000 delegation filter - now automatic based on valid CIR
    const hasValidCir = property.cir && property.cir.length >= 3 && isRegionWithDelegation(property);
    const matchesDelegation = delegationFilter === 'all' ||
                             (delegationFilter === 'with' && hasValidCir) ||
                             (delegationFilter === 'without' && !hasValidCir);
    
    // Questura credentials filter - use the computed field from backend
    const hasQuesturaCredentials = property.has_questura_credentials;
    const matchesQuestura = questuraFilter === 'all' ||
                           (questuraFilter === 'with' && hasQuesturaCredentials) ||
                           (questuraFilter === 'without' && !hasQuesturaCredentials);
    
    // NEW: Region filter — confronto diretto con property.region (Title Case dopo migration 041)
    const matchesRegion = regionFilter === 'all' || (() => {
      const propRegion = (property.region || '').trim();
      if (propRegion === regionFilter) return true;
      // Fallback: deriva da CIR per immobili senza region (raro dopo migration 041)
      const fromCir = getRegionFromCir(property.cir);
      return fromCir && fromCir.key === regionFilter;
    })();
    
    return matchesSearch && matchesStatus && matchesCompleteness && matchesManager && matchesDelegation && matchesQuestura && matchesRegion;
  });

  // Sort properties
  const sortedProperties = [...filteredProperties].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortField) {
      case 'name':
        aValue = a.name?.toLowerCase() || '';
        bValue = b.name?.toLowerCase() || '';
        break;
      case 'code':
        aValue = a.property_code?.toLowerCase() || '';
        bValue = b.property_code?.toLowerCase() || '';
        break;
      case 'city':
        aValue = a.city?.toLowerCase() || '';
        bValue = b.city?.toLowerCase() || '';
        break;
      case 'type':
        aValue = a.property_type?.toLowerCase() || '';
        bValue = b.property_type?.toLowerCase() || '';
        break;
      case 'status':
        aValue = a.status?.toLowerCase() || '';
        bValue = b.status?.toLowerCase() || '';
        break;
      case 'created':
        aValue = a.created_at || (sortDirection === 'asc' ? '9999' : '');
        bValue = b.created_at || (sortDirection === 'asc' ? '9999' : '');
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedProperties.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProperties = sortedProperties.slice(startIndex, endIndex);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  if (loading) {
    return (
      <Layout title="Gestione Immobili">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Gestione Immobili">
      <div className="space-y-6 fade-in">
        {/* Banner avanzamento import massivo dal vecchio sito */}
        {bulkImport?.status === 'running' && (
          <Card className="p-4 border border-blue-200 bg-blue-50 shadow-sm" data-testid="bulk-import-progress">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-blue-700" />
                <div>
                  <p className="text-sm font-semibold text-blue-900" data-testid="bulk-import-progress-text">
                    Import massivo dal vecchio sito: {bulkImport.done || 0} di {bulkImport.total || 0}
                    {bulkImport.current ? ` — in corso: ${bulkImport.current}` : ''}
                  </p>
                  <p className="text-xs text-blue-700">
                    {bulkImport.ok || 0} importati · {bulkImport.failed || 0} falliti
                    {bulkImport.started_by === 'auto-notturno' ? ' · avviato automaticamente (notturno)' : ''} — al termine riceverai una email di report
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={cancelBulkImport} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="bulk-import-cancel-btn">
                <X className="h-4 w-4 mr-1" /> Annulla
              </Button>
            </div>
            <div className="mt-2 h-2 w-full bg-blue-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${bulkImport.total ? Math.round(((bulkImport.done || 0) / bulkImport.total) * 100) : 0}%` }} />
            </div>
          </Card>
        )}
        {/* Header with actions */}
        <Card className="p-6 border-0 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                Immobili Pubblicati
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-base font-semibold rounded-full">
                  {properties.length}
                </span>
                {propertiesWithoutValidManager > 0 && (
                  <span 
                    className="px-3 py-1 bg-red-100 text-red-800 text-sm font-semibold rounded-full cursor-pointer hover:bg-red-200 transition-colors"
                    onClick={() => setManagerFilter(managerFilter === 'without' ? 'all' : 'without')}
                    title="Clicca per filtrare"
                  >
                    ⚠️ {propertiesWithoutValidManager} senza responsabile
                  </span>
                )}
              </h2>
              <p className="text-sm text-slate-600">Gestisci il catalogo degli immobili</p>
            </div>
            
            <div className="flex gap-3 items-center flex-wrap">
              <VetrinaSlotsControl />
              <Button 
                variant="outline"
                onClick={startBulkImport}
                disabled={bulkImport?.status === 'running'}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="bulk-import-old-site-btn"
                title="Importa scheda, foto e listini dal vecchio sito per tutti gli immobili (sequenziale, email di report al termine)"
              >
                <DownloadCloud className="h-4 w-4 mr-2" />
                Importa dal vecchio sito
              </Button>
              <Button 
                variant="outline"
                onClick={openDuplicatesDialog}
                className="border-amber-200 text-amber-700 hover:bg-amber-50"
                data-testid="find-duplicates-btn"
              >
                <Copy className="h-4 w-4 mr-2" />
                Trova Duplicati
              </Button>
              <Button 
                variant="outline"
                onClick={openSuspendedDialog}
                className="border-orange-300 text-orange-600 hover:bg-orange-50"
                data-testid="view-suspended-btn"
              >
                <Pause className="h-4 w-4 mr-2" />
                Sospesi
              </Button>
              <Button 
                variant="outline"
                onClick={openArchivedDialog}
                className="border-slate-300 text-slate-600 hover:bg-slate-50"
                data-testid="view-archived-btn"
              >
                <Archive className="h-4 w-4 mr-2" />
                Archiviati
              </Button>
              <Button 
                variant="outline"
                onClick={() => openAssociationDialog()}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="bulk-associate-btn"
              >
                <Link className="h-4 w-4 mr-2" />
                Associa Bulk
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowStatusManager(true)}
                className="border-orange-200 text-orange-700 hover:bg-orange-50"
              >
                Gestisci Chiusure e Cessazioni
              </Button>
              <Dialog open={showNewProperty} onOpenChange={(open) => {
                if (!open) {
                  // Reset form quando si chiude il dialog
                  setNewProperty({
                    property_code: '', name: '', address: '', country: 'Italia', region: '', province: '', city: '',
                    postal_code: '', property_type: '', bedrooms: 1, bathrooms: 1, max_guests: 2,
                    cleaning_cost: 0, linen_cost_per_person: 0, apply_submultiples: false, available_submultiples: [],
                    partner_code: 'none', property_manager_id: 'none', police_station_code: '', tourism_observatory: '',
                    booking_property_id: '', airbnb_property_id: '', holidayworld_url: '', cin: '', cir: '', intervention_budget: 0,
                    last_minute_enabled: false, last_minute_discount: 10, owner_name: '', owner_email: '', owner_birth_date: '',
                    alloggiati_enabled: false, alloggiati_username: '', alloggiati_password: '', alloggiati_wskey: '',
                    alloggiati_structure_code: '', alloggiati_apartment_id: '', alloggiati_structure_type: '', alloggiati_beds_count: ''
                  });
                }
                setShowNewProperty(open);
              }}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-blue-900 hover:bg-blue-800"
                    data-testid="add-property-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nuovo Immobile
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Nuovo Immobile</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateProperty} className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Codice Immobile *</Label>
                      <Input
                        value={newProperty.property_code}
                        onChange={(e) => setNewProperty({...newProperty, property_code: e.target.value.toUpperCase()})}
                        required
                        placeholder="IPA22"
                        maxLength={10}
                        data-testid="property-code-input"
                        className="uppercase"
                      />
                      <p className="text-xs text-gray-500 mt-1">Es: IPA22, ILA194, ISI731</p>
                    </div>
                    <div>
                      <Label>Nome Immobile</Label>
                      <Input
                        value={newProperty.name}
                        onChange={(e) => setNewProperty({...newProperty, name: e.target.value})}
                        required
                        placeholder="Villa del Mare"
                        data-testid="property-name-input"
                      />
                    </div>
                    <div>
                      <Label>Tipo Immobile</Label>
                      <Select 
                        value={newProperty.property_type}
                        onValueChange={(value) => setNewProperty({...newProperty, property_type: value})}
                      >
                        <SelectTrigger data-testid="property-type-select">
                          <SelectValue placeholder="Seleziona tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="apartment">Appartamento</SelectItem>
                          <SelectItem value="villa">Villa</SelectItem>
                          <SelectItem value="house">Casa</SelectItem>
                          <SelectItem value="studio">Monolocale</SelectItem>
                          <SelectItem value="room">Stanza</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Indirizzo</Label>
                      <Input
                        value={newProperty.address}
                        onChange={(e) => setNewProperty({...newProperty, address: e.target.value})}
                        required
                        placeholder="Via Roma, 123"
                        data-testid="address-input"
                      />
                    </div>
                    <div>
                      <Label>Stato *</Label>
                      <SearchableSelect
                        options={countries}
                        value={newProperty.country}
                        onValueChange={handleCountryChange}
                        placeholder="Seleziona o inserisci stato"
                        allowCustom={true}
                      />
                      <p className="text-xs text-gray-500 mt-1">Seleziona dalla lista o aggiungi nuovo</p>
                    </div>
                    <div>
                      <Label>Regione *</Label>
                      <SearchableSelect
                        options={regions}
                        value={newProperty.region}
                        onValueChange={handleRegionChange}
                        placeholder="Seleziona o inserisci regione"
                        allowCustom={true}
                      />
                      <p className="text-xs text-gray-500 mt-1">Seleziona dalla lista o aggiungi nuovo</p>
                    </div>
                    <div>
                      <Label>Provincia</Label>
                      <Input
                        value={newProperty.province}
                        onChange={(e) => setNewProperty({...newProperty, province: e.target.value.toUpperCase()})}
                        placeholder="Es: GE, MI, RM"
                        maxLength="2"
                        className="uppercase"
                      />
                      <p className="text-xs text-gray-500 mt-1">Sigla provincia (es: GE, MI, RM)</p>
                    </div>
                    <div>
                      <Label>Città *</Label>
                      <SearchableSelect
                        options={cities}
                        value={newProperty.city}
                        onValueChange={handleCityChange}
                        placeholder="Seleziona o inserisci città"
                        allowCustom={true}
                      />
                      <p className="text-xs text-gray-500 mt-1">Seleziona dalla lista o aggiungi nuovo</p>
                    </div>
                    <div>
                      <Label>CAP</Label>
                      <Input
                        value={newProperty.postal_code}
                        onChange={(e) => setNewProperty({...newProperty, postal_code: e.target.value})}
                        required
                        placeholder="20100"
                        data-testid="postal-code-input"
                      />
                    </div>
                    {/* Prezzo per notte rimosso */}
                    <div>
                      <Label>Costo Pulizie (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newProperty.cleaning_cost}
                        onChange={(e) => setNewProperty({...newProperty, cleaning_cost: e.target.value})}
                        required
                        placeholder="50.00"
                        data-testid="cleaning-cost-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Questo importo si auto-compilerà nelle prenotazioni
                      </p>
                    </div>
                    <div>
                      <Label>Costo Biancheria a Persona (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newProperty.linen_cost_per_person || 0}
                        onChange={(e) => setNewProperty({...newProperty, linen_cost_per_person: parseFloat(e.target.value) || 0})}
                        placeholder="0.00"
                        data-testid="linen-cost-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Costo per ospite (auto-moltiplicato nelle prenotazioni)
                      </p>
                    </div>
                  </div>

                  {/* Property Details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Camere da Letto</Label>
                      <Input
                        type="number"
                        min="0"
                        value={newProperty.bedrooms}
                        onChange={(e) => setNewProperty({...newProperty, bedrooms: e.target.value})}
                        required
                        data-testid="bedrooms-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">0 = Solo divano letto nel soggiorno</p>
                    </div>
                    <div>
                      <Label>Bagni</Label>
                      <Input
                        type="number"
                        min="1"
                        value={newProperty.bathrooms}
                        onChange={(e) => setNewProperty({...newProperty, bathrooms: e.target.value})}
                        required
                        data-testid="bathrooms-input"
                      />
                    </div>
                    <div>
                      <Label>Ospiti Massimi</Label>
                      <Input
                        type="number"
                        min="1"
                        value={newProperty.max_guests}
                        onChange={(e) => setNewProperty({...newProperty, max_guests: e.target.value})}
                        required
                        data-testid="max-guests-input"
                      />
                    </div>

                    
                    {/* Sottomultipli Configuration */}
                    <div className="col-span-2">
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <input
                            type="checkbox"
                            id="apply_submultiples"
                            checked={newProperty.apply_submultiples}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setNewProperty(prev => {
                                const maxGuests = parseInt(prev.max_guests) || 2;
                                let defaultSubs = [];
                                
                                if (checked) {
                                  // Calculate default submultiples
                                  defaultSubs.push(maxGuests); // Always include max
                                  if (maxGuests >= 4) defaultSubs.push(maxGuests - 2);
                                  if (maxGuests >= 6) defaultSubs.push(maxGuests - 4);
                                }
                                
                                return {
                                  ...prev,
                                  apply_submultiples: checked,
                                  available_submultiples: defaultSubs
                                };
                              });
                            }}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="apply_submultiples" className="cursor-pointer font-semibold">
                            Applica Sottomultipli
                          </Label>
                        </div>
                        
                        {newProperty.apply_submultiples && (
                          <div className="space-y-3">
                            <p className="text-sm text-blue-900">
                              Posti letto disponibili per prenotazione (seleziona le opzioni che vuoi offrire):
                            </p>
                            
                            <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                              {Array.from({ length: parseInt(newProperty.max_guests) || 2 }, (_, i) => i + 1).map(num => {
                                const isChecked = newProperty.available_submultiples.includes(num);
                                const isDefault = (
                                  num === parseInt(newProperty.max_guests) ||
                                  num === parseInt(newProperty.max_guests) - 2 ||
                                  num === parseInt(newProperty.max_guests) - 4
                                );
                                
                                return (
                                  <div key={num} className={`
                                    p-2 border-2 rounded cursor-pointer text-center transition-all
                                    ${isChecked 
                                      ? 'border-blue-600 bg-blue-100' 
                                      : 'border-slate-300 bg-white hover:border-blue-400'
                                    }
                                  `}>
                                    <input
                                      type="checkbox"
                                      id={`sub_${num}`}
                                      checked={isChecked}
                                      onChange={(e) => {
                                        setNewProperty(prev => ({
                                          ...prev,
                                          available_submultiples: e.target.checked
                                            ? [...prev.available_submultiples, num].sort((a, b) => b - a)
                                            : prev.available_submultiples.filter(n => n !== num)
                                        }));
                                      }}
                                      className="hidden"
                                    />
                                    <label htmlFor={`sub_${num}`} className="cursor-pointer block">
                                      <div className="font-bold text-lg">{num}</div>
                                      {isDefault && <div className="text-xs text-blue-600">Default</div>}
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                            
                            <p className="text-xs text-slate-600">
                              ℹ️ Default: massimo + (-2) + (-4). Puoi selezionare manualmente altre classi.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                  </div>

                  {/* Removed Description and Amenities sections */}

                  {/* Responsible Manager */}
                  {/* Partner & Manager */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Partner Acquisitore</Label>
                      <Select 
                        value={newProperty.partner_code}
                        onValueChange={(value) => {
                          const stillValid = getSegnalatoriForPartner(value).some(s => s.segnalatore_id === newProperty.segnalatore_id);
                          setNewProperty({...newProperty, partner_code: value, segnalatore_id: stillValid ? newProperty.segnalatore_id : 'none'});
                        }}
                      >
                        <SelectTrigger data-testid="partner-select">
                          <SelectValue placeholder="Seleziona partner (opzionale)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nessun partner</SelectItem>
                          {partners.filter(p => p.partner_code).map(partner => (
                            <SelectItem key={partner.id} value={partner.partner_code || partner.id}>
                              {partner.name} ({partner.partner_code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">
                        👤 Partner che ha acquisito l'immobile
                      </p>
                    </div>
                    <div>
                      <Label>Responsabile Gestione</Label>
                      <SearchableSelect
                        options={[
                          { value: "none", label: "Nessun responsabile" },
                          { value: "self", label: "🏠 Proprietà (auto-gestito)" },
                          ...propertyManagers.filter(m => m.id && !m.archived).map(manager => ({
                            value: manager.id,
                            label: `${manager.first_name} ${manager.last_name}${manager.phone ? ` (${manager.phone})` : ''}`
                          }))
                        ]}
                        value={newProperty.property_manager_id || "none"}
                        onValueChange={(value) => setNewProperty({...newProperty, property_manager_id: value === "none" ? null : value})}
                        placeholder="Seleziona responsabile (opzionale)"
                        searchPlaceholder="Cerca responsabile..."
                        allowCustom={false}
                        data-testid="manager-select"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        👨‍💼 Responsabile operativo dell'immobile (cerca per nome o telefono)
                      </p>
                    </div>
                  </div>

                  {/* Segnalatore di provenienza (HW Academy - Rete Segnalatori) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Segnalatore di provenienza</Label>
                      <SearchableSelect
                        options={[
                          { value: "none", label: "Nessun segnalatore" },
                          ...getSegnalatoriForPartner(newProperty.partner_code).map(s => ({
                            value: s.segnalatore_id,
                            label: `${s.nome || ''} ${s.cognome || ''}${s.email ? ` (${s.email})` : ''}`.trim()
                          }))
                        ]}
                        value={newProperty.segnalatore_id || "none"}
                        onValueChange={handleSegnalatoreChange}
                        placeholder="Seleziona segnalatore (opzionale)"
                        searchPlaceholder="Cerca segnalatore..."
                        allowCustom={false}
                        data-testid="segnalatore-select"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        🔗 HW Academy — chi ha segnalato l'immobile (Rete Segnalatori)
                      </p>
                    </div>
                  </div>

                  {/* Police Station */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Codice Questura (Alloggiatiweb)</Label>
                      <Input
                        value={newProperty.police_station_code || ''}
                        onChange={(e) => setNewProperty({...newProperty, police_station_code: e.target.value})}
                        placeholder="es. 001234"
                        data-testid="police-code-input"
                      />
                    </div>
                    <div>
                      <Label>Osservatorio Turistico</Label>
                      <Input
                        value={newProperty.tourism_observatory || ''}
                        onChange={(e) => setNewProperty({...newProperty, tourism_observatory: e.target.value})}
                        placeholder="es. Osservatorio Lombardia"
                        data-testid="tourism-observatory-input"
                      />
                    </div>
                  </div>

                  {/* Platform Property IDs */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>ID Proprietà Booking.com</Label>
                      <Input
                        type="text"
                        value={newProperty.booking_property_id || ''}
                        onChange={(e) => setNewProperty({...newProperty, booking_property_id: e.target.value})}
                        placeholder="es: 1234567"
                        data-testid="booking-property-id-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        🔑 <strong>Importante:</strong> ID dalla extranet Booking.com per associare recensioni
                      </p>
                    </div>
                    <div>
                      <Label>ID Proprietà Airbnb</Label>
                      <Input
                        type="text"
                        value={newProperty.airbnb_property_id || ''}
                        onChange={(e) => setNewProperty({...newProperty, airbnb_property_id: e.target.value})}
                        placeholder="es: 98765432"
                        data-testid="airbnb-property-id-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        🔑 <strong>Importante:</strong> ID dalla dashboard Airbnb per associare recensioni
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <Label>Link casa su holidayworld.it</Label>
                      <Input
                        type="url"
                        value={newProperty.holidayworld_url || ''}
                        onChange={(e) => setNewProperty({...newProperty, holidayworld_url: e.target.value})}
                        placeholder="https://www.holidayworld.it/..."
                        data-testid="holidayworld-url-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        📬 Usato come destinazione del pulsante nelle <strong>newsletter</strong> (finché i due siti non si parlano)
                      </p>
                    </div>
                  </div>

                  {/* Codici Identificativi e Dati Proprietario */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-semibold text-slate-700 mb-3">📋 Codici Identificativi & Proprietario</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>CIN (Codice Identificativo Nazionale)</Label>
                        <Input
                          type="text"
                          value={newProperty.cin || ''}
                          onChange={(e) => setNewProperty({...newProperty, cin: e.target.value.toUpperCase()})}
                          placeholder="IT069050C25M8THUMX"
                          className="uppercase"
                        />
                      </div>
                      <div>
                        <Label>CIR (Codice Identificativo Regionale)</Label>
                        <Input
                          type="text"
                          value={newProperty.cir || ''}
                          onChange={(e) => {
                            const cir = e.target.value.toUpperCase();
                            const oldCir = (newProperty.cir || '').toUpperCase();
                            const updates = { cir };
                            // Auto-sync CIR da Delega se vuoto o se uguale al vecchio CIR (non modificato manualmente)
                            const currentDelegaCir = (newProperty.ross1000_cir_from_delegation || '').toUpperCase();
                            if (!currentDelegaCir || currentDelegaCir === oldCir) {
                              updates.ross1000_cir_from_delegation = cir;
                            }
                            setNewProperty({ ...newProperty, ...updates });
                          }}
                          placeholder="069074CVP0031"
                          className="uppercase"
                        />
                      </div>
                      <div>
                        <Label>Nome Proprietario</Label>
                        <Input
                          type="text"
                          value={newProperty.owner_name || ''}
                          onChange={(e) => setNewProperty({...newProperty, owner_name: e.target.value})}
                          placeholder="Mario Rossi"
                        />
                      </div>
                      <div>
                        <Label>Email Proprietario</Label>
                        <Input
                          type="email"
                          value={newProperty.owner_email || ''}
                          onChange={(e) => setNewProperty({...newProperty, owner_email: e.target.value})}
                          placeholder="proprietario@email.com"
                        />
                      </div>
                      <div>
                        <Label>Data di nascita Proprietario</Label>
                        <Input
                          data-testid="owner-birth-date-create"
                          type="date"
                          value={newProperty.owner_birth_date || ''}
                          onChange={(e) => setNewProperty({...newProperty, owner_birth_date: e.target.value})}
                        />
                        <p className="text-xs text-slate-500 mt-1">Per gli auguri di compleanno automatici</p>
                      </div>
                    </div>
                  </div>

                  {/* Modalità pagamento al proprietario - CREATE */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-semibold text-slate-700 mb-3">💳 Modalità pagamento al proprietario</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Modalità</Label>
                        <select
                          data-testid="owner-payment-method-create"
                          value={newProperty.owner_payment_method || 'bank_transfer'}
                          onChange={(e) => setNewProperty({...newProperty, owner_payment_method: e.target.value})}
                          className="w-full mt-1 px-3 py-2 border rounded-md text-sm bg-white"
                        >
                          <option value="bank_transfer">Bonifico SEPA (Holiday World)</option>
                          <option value="paypal">PayPal (gestito da HW Smart)</option>
                        </select>
                        <p className="text-xs text-slate-500 mt-1">
                          {(newProperty.owner_payment_method || 'bank_transfer') === 'paypal'
                            ? 'I pagamenti compariranno nella sezione "PayPal da pagare"'
                            : 'Gestito dal sistema bonifici di Holiday World (default)'}
                        </p>
                      </div>
                      {newProperty.owner_payment_method === 'paypal' && (
                        <div>
                          <Label>Account PayPal proprietario</Label>
                          <Input
                            data-testid="owner-paypal-email-create"
                            type="text"
                            value={newProperty.owner_paypal_email || ''}
                            onChange={(e) => setNewProperty({...newProperty, owner_paypal_email: e.target.value})}
                            placeholder="proprietario@paypal.com oppure @handle"
                          />
                          <p className="text-xs text-slate-500 mt-1">Email, telefono o handle (es. @nome) dell'account PayPal del proprietario. Può essere completato in seguito.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Budget Interventi e Last Minute */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-semibold text-slate-700 mb-3">💶 Budget & Last Minute</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Budget Interventi Autorizzato (€)</Label>
                        <Input
                          type="number"
                          value={newProperty.intervention_budget || ''}
                          onChange={(e) => setNewProperty({...newProperty, intervention_budget: parseFloat(e.target.value) || 0})}
                          placeholder="0"
                          min="0"
                          step="50"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Budget massimo autorizzato dal proprietario per interventi
                        </p>
                      </div>
                      <div>
                        <Label>Last Minute Abilitato</Label>
                        <div className="flex items-center space-x-2 mt-2">
                          <input
                            type="checkbox"
                            checked={newProperty.last_minute_enabled || false}
                            onChange={(e) => setNewProperty({...newProperty, last_minute_enabled: e.target.checked})}
                            className="h-4 w-4 text-blue-600 rounded"
                          />
                          <span className="text-sm text-slate-600">
                            {newProperty.last_minute_enabled ? '✅ Abilitato' : '❌ Disabilitato'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* AlloggiatiWeb Integration */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-semibold text-slate-700 mb-3">🏛️ Integrazione AlloggiatiWeb (Questura)</h4>
                    <div className="bg-blue-50 p-3 rounded-lg mb-4">
                      <p className="text-sm text-blue-700">
                        Configura le credenziali per l'invio automatico delle schedine alloggiati alla Questura.
                        Le credenziali si ottengono registrandosi sul portale <a href="https://alloggiatiweb.poliziadistato.it" target="_blank" rel="noopener noreferrer" className="underline">AlloggiatiWeb</a>.
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2 mb-4">
                      <input
                        type="checkbox"
                        checked={newProperty.alloggiati_enabled || false}
                        onChange={(e) => setNewProperty({...newProperty, alloggiati_enabled: e.target.checked})}
                        className="h-4 w-4 text-blue-600 rounded"
                        data-testid="alloggiati-enabled"
                      />
                      <span className="text-sm font-medium text-slate-700">
                        {newProperty.alloggiati_enabled ? '✅ AlloggiatiWeb Abilitato' : '❌ AlloggiatiWeb Disabilitato'}
                      </span>
                      {selectedProperty && (
                        <button
                          type="button"
                          onClick={() => openCredAudit(selectedProperty.id)}
                          className="ml-auto text-xs text-blue-600 hover:text-blue-800 underline"
                          data-testid="cred-audit-open-btn"
                        >
                          📜 Storico credenziali
                        </button>
                      )}
                    </div>
                    
                    {newProperty.alloggiati_enabled && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Username Portale</Label>
                          <Input
                            type="text"
                            value={newProperty.alloggiati_username || ''}
                            onChange={(e) => {
                              const username = e.target.value;
                              const oldUsername = newProperty.alloggiati_username || '';
                              // Auto-aggiorna codice struttura e ID appartamento se:
                              // 1. Sono vuoti, oppure
                              // 2. Sono uguali al vecchio username (non modificati manualmente)
                              const updates = { alloggiati_username: username };
                              if (!newProperty.alloggiati_structure_code || newProperty.alloggiati_structure_code === oldUsername) {
                                updates.alloggiati_structure_code = username;
                              }
                              if (!newProperty.alloggiati_apartment_id || newProperty.alloggiati_apartment_id === oldUsername) {
                                updates.alloggiati_apartment_id = username;
                              }
                              setNewProperty({...newProperty, ...updates});
                            }}
                            placeholder="Username Questura"
                            name="alloggiati-user-nofill"
                            autoComplete="off"
                            data-testid="alloggiati-username"
                          />
                        </div>
                        <div>
                          <Label>Password Portale</Label>
                          <div className="relative">
                            <Input
                              type={showPassword.alloggiati_password ? "text" : "password"}
                              value={newProperty.alloggiati_password || ''}
                              onChange={(e) => setNewProperty({...newProperty, alloggiati_password: e.target.value})}
                              placeholder="••••••••"
                              name="alloggiati-pass-nofill"
                              autoComplete="new-password"
                              data-testid="alloggiati-password"
                              className="pr-10"
                            />
                            <button
                              type="button"
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              onClick={() => setShowPassword(prev => ({ ...prev, alloggiati_password: !prev.alloggiati_password }))}
                              data-testid="toggle-password-visibility-create"
                            >
                              {showPassword.alloggiati_password ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <Label>Web Service Key (WSKEY)</Label>
                          <Input
                            type="text"
                            value={newProperty.alloggiati_wskey || ''}
                            onChange={(e) => setNewProperty({...newProperty, alloggiati_wskey: e.target.value})}
                            placeholder="Chiave generata dal portale"
                            data-testid="alloggiati-wskey"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            Genera la chiave dal menu "Supporto" → "Chiave Web Service"
                          </p>
                        </div>
                        <div>
                          <Label>Codice Struttura</Label>
                          <Input
                            type="text"
                            value={newProperty.alloggiati_structure_code || ''}
                            onChange={(e) => setNewProperty({...newProperty, alloggiati_structure_code: e.target.value})}
                            placeholder="Codice assegnato dalla Questura"
                            data-testid="alloggiati-structure-code"
                          />
                        </div>
                        <div>
                          <Label>Tipo Struttura</Label>
                          <Select
                            value={newProperty.alloggiati_structure_type || ''}
                            onValueChange={(value) => setNewProperty({...newProperty, alloggiati_structure_type: value})}
                          >
                            <SelectTrigger data-testid="alloggiati-structure-type">
                              <SelectValue placeholder="Seleziona tipo" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="albergo">Albergo</SelectItem>
                              <SelectItem value="bb">B&B</SelectItem>
                              <SelectItem value="affittacamere">Affittacamere</SelectItem>
                              <SelectItem value="casa_vacanze">Casa Vacanze</SelectItem>
                              <SelectItem value="appartamento">Appartamento</SelectItem>
                              <SelectItem value="residence">Residence</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>ID Appartamento</Label>
                          <Input
                            type="text"
                            value={newProperty.alloggiati_apartment_id || ''}
                            onChange={(e) => setNewProperty({...newProperty, alloggiati_apartment_id: e.target.value.toUpperCase()})}
                            placeholder="Es: GE0459678"
                            data-testid="alloggiati-apartment-id"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            Per strutture multi-unità (opzionale)
                          </p>
                        </div>
                        <div>
                          <Label>Posti Letto Registrati</Label>
                          <Input
                            type="number"
                            value={newProperty.alloggiati_beds_count || ''}
                            onChange={(e) => setNewProperty({...newProperty, alloggiati_beds_count: parseInt(e.target.value) || ''})}
                            placeholder="Numero posti letto"
                            min="1"
                            data-testid="alloggiati-beds-count"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ISTAT / ROSS1000 Integration - CREATE */}
                  <div className="border-t pt-4 mt-4" data-testid="create-ross1000-section">
                    <h4 className="font-semibold text-slate-700 mb-3">🗺️ Integrazione ISTAT / ROSS1000 (Flussi Turistici)</h4>
                    <div className="bg-emerald-50 p-3 rounded-lg mb-4">
                      <p className="text-sm text-emerald-700">
                        Configura le credenziali per l'invio automatico dei flussi turistici al portale regionale
                        (ROSS1000 / GIES / Osservatori). Queste credenziali sono <strong>indipendenti</strong> da quelle della Questura.
                      </p>
                    </div>

                    <div className="flex items-center space-x-2 mb-4">
                      <input
                        type="checkbox"
                        checked={newProperty.has_ross1000_delegation || false}
                        onChange={(e) => setNewProperty({...newProperty, has_ross1000_delegation: e.target.checked})}
                        className="h-4 w-4 text-emerald-600 rounded"
                        data-testid="create-ross1000-enabled"
                      />
                      <span className="text-sm font-medium text-slate-700">
                        {newProperty.has_ross1000_delegation ? '✅ Delega ROSS1000/ISTAT Attiva' : '❌ Nessuna Delega ROSS1000/ISTAT'}
                      </span>
                    </div>

                    {newProperty.has_ross1000_delegation && (() => {
                      const groupCreds = getGroupCredsForProvince(newProperty.province);
                      const mode = newProperty.ross1000_credentials_mode || (groupCreds ? 'group' : 'individual');
                      const effectiveUsername = mode === 'group' && groupCreds ? groupCreds.username : (newProperty.ross1000_username || '');
                      const effectivePassword = mode === 'group' && groupCreds ? groupCreds.password : (newProperty.ross1000_password || '');
                      return (
                        <>
                          <div className="mb-4 bg-white border rounded-lg p-3">
                            <Label className="mb-2 block">Tipo Credenziali ROSS1000</Label>
                            <div className="flex flex-col md:flex-row gap-3">
                              <label className={`flex-1 flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition ${mode === 'group' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'} ${!groupCreds ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                <input
                                  type="radio"
                                  name="create-ross1000-mode"
                                  value="group"
                                  checked={mode === 'group'}
                                  disabled={!groupCreds}
                                  onChange={() => setNewProperty({...newProperty, ross1000_credentials_mode: 'group'})}
                                  className="mt-1"
                                  data-testid="create-ross1000-mode-group"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-sm">👥 Account di Gruppo (provincia)</div>
                                  <div className="text-xs text-slate-500 mt-1">
                                    {groupCreds
                                      ? `Usa le credenziali condivise per ${groupCreds.province} (${groupCreds.region})`
                                      : (
                                        <span>
                                          Nessun account di gruppo per la provincia "{newProperty.province || '-'}".{' '}
                                          <button
                                            type="button"
                                            className="text-blue-600 hover:text-blue-800 underline font-medium"
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openQuickGroupDialog(); }}
                                            data-testid="quick-create-group-cred-btn-create"
                                          >
                                            ➕ Crealo ora
                                          </button>
                                        </span>
                                      )}
                                  </div>
                                </div>
                              </label>
                              <label className={`flex-1 flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition ${mode === 'individual' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                                <input
                                  type="radio"
                                  name="create-ross1000-mode"
                                  value="individual"
                                  checked={mode === 'individual'}
                                  onChange={() => setNewProperty({...newProperty, ross1000_credentials_mode: 'individual'})}
                                  className="mt-1"
                                  data-testid="create-ross1000-mode-individual"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-sm">🔑 Credenziali Individuali</div>
                                  <div className="text-xs text-slate-500 mt-1">
                                    Il proprietario ha un proprio account ROSS1000
                                  </div>
                                </div>
                              </label>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label>Username ROSS1000 / GIES</Label>
                              <Input
                                type="text"
                                value={effectiveUsername}
                                onChange={(e) => setNewProperty({...newProperty, ross1000_username: e.target.value})}
                                placeholder="Username portale regionale"
                                name="ross1000-user-nofill"
                                autoComplete="off"
                                data-testid="create-ross1000-username"
                                readOnly={mode === 'group'}
                                className={mode === 'group' ? 'bg-slate-100 cursor-not-allowed' : ''}
                              />
                              {mode === 'group' && groupCreds && (
                                <p className="text-xs text-emerald-700 mt-1">
                                  🔒 Da account di gruppo <strong>{groupCreds.province}</strong>
                                </p>
                              )}
                            </div>
                            <div>
                              <Label>Password ROSS1000 / GIES</Label>
                              <div className="relative">
                                <Input
                                  type={showPassword.ross1000_password ? "text" : "password"}
                                  value={effectivePassword}
                                  onChange={(e) => setNewProperty({...newProperty, ross1000_password: e.target.value})}
                                  placeholder="••••••••"
                                  name="ross1000-pass-nofill"
                                  autoComplete="new-password"
                                  className={`pr-10 ${mode === 'group' ? 'bg-slate-100 cursor-not-allowed' : ''}`}
                                  data-testid="create-ross1000-password"
                                  readOnly={mode === 'group'}
                                />
                                <button
                                  type="button"
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                  onClick={() => setShowPassword(prev => ({ ...prev, ross1000_password: !prev.ross1000_password }))}
                                  data-testid="toggle-ross1000-password-create"
                                >
                                  {showPassword.ross1000_password ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                              </div>
                            </div>
                            <div>
                              <Label>Codice Struttura / ID Immobile ROSS1000</Label>
                              <Input
                                type="text"
                                value={newProperty.ross1000_structure_code || ''}
                                onChange={(e) => setNewProperty({...newProperty, ross1000_structure_code: e.target.value})}
                                placeholder="Lascia vuoto se non assegnato"
                                data-testid="create-ross1000-structure-code"
                              />
                              <p className="text-xs text-slate-500 mt-1">
                                Se vuoto verrà considerato <code>0</code> e non verrà inviato al portale
                              </p>
                            </div>
                            <div>
                              <Label>CIR da Delega</Label>
                              <Input
                                type="text"
                                value={newProperty.ross1000_cir_from_delegation || ''}
                                onChange={(e) => setNewProperty({...newProperty, ross1000_cir_from_delegation: e.target.value.toUpperCase()})}
                                placeholder="CIR registrato nel file delega"
                                className="uppercase"
                                data-testid="create-ross1000-cir"
                              />
                              <p className="text-xs text-slate-500 mt-1">
                                Copiato automaticamente dal CIR sopra, modificabile solo se diverso dal file delega regionale
                              </p>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Images field removed */}

                  <div className="flex justify-end space-x-2 pt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowNewProperty(false)}
                    >
                      Annulla
                    </Button>
                    <Button 
                      type="submit"
                      className="bg-blue-900 hover:bg-blue-800"
                      data-testid="save-property-btn"
                    >
                      Crea Immobile
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <Card className="p-4 border-0 shadow-lg">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                <Input
                  placeholder="Cerca per nome, codice, città o indirizzo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-properties-input"
                />
              </div>
            </div>
            <div className="w-full md:w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  <SelectItem value="active">Attivi</SelectItem>
                  <SelectItem value="terminated">Cessati</SelectItem>
                  <SelectItem value="archived">Archiviati</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full md:w-48">
              <Select value={completenessFilter} onValueChange={setCompletenessFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Stato dati" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="complete">✅ Completi</SelectItem>
                  <SelectItem value="incomplete">⚠️ Incompleti</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full md:w-48">
              <Select value={managerFilter} onValueChange={setManagerFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Responsabile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="with">👤 Con Responsabile</SelectItem>
                  <SelectItem value="without">⚠️ Senza Responsabile</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full md:w-48">
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger data-testid="region-filter-select">
                  <SelectValue placeholder="Regione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le Regioni</SelectItem>
                  {regionsWithProperties.map(region => {
                    const statusIcon = region.portal_status === 'ready' ? '🟢' :
                                       region.portal_status === 'manual' ? '🟡' : '🔴';
                    const statusTitle = region.portal_status === 'ready' ? 'Portale automatico pronto' :
                                        region.portal_status === 'manual' ? 'Portale manuale (file C/59 o caricamento web)' :
                                        'Portale non supportato';
                    return (
                      <SelectItem key={region.key} value={region.key} title={statusTitle}>
                        <span className="inline-flex items-center gap-1.5">
                          <span aria-hidden>{statusIcon}</span>
                          <span>{region.name} ({region.total_properties})</span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full md:w-48">
              <Select value={delegationFilter} onValueChange={setDelegationFilter}>
                <SelectTrigger data-testid="delegation-filter-select">
                  <SelectValue placeholder="Dati ISTAT" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ISTAT: Tutti</SelectItem>
                  <SelectItem value="with">✅ Con CIR valido</SelectItem>
                  <SelectItem value="without">❌ Senza CIR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full md:w-48">
              <Select value={questuraFilter} onValueChange={setQuesturaFilter}>
                <SelectTrigger data-testid="questura-filter-select">
                  <SelectValue placeholder="Credenziali Questura" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Questura: Tutti</SelectItem>
                  <SelectItem value="with">✅ Con Credenziali</SelectItem>
                  <SelectItem value="without">❌ Senza Credenziali</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Properties Table */}
        <Card className="overflow-hidden shadow-lg">
          {filteredProperties.length === 0 ? (
            <div className="p-8 text-center">
              <Building className="h-12 w-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">Nessun immobile trovato</h3>
              <p className="text-slate-500">
                {searchTerm || statusFilter !== 'all' || managerFilter !== 'all' || delegationFilter !== 'all' || questuraFilter !== 'all'
                  ? 'Prova a modificare i filtri di ricerca' 
                  : 'Inizia aggiungendo il primo immobile'
                }
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                  <thead className="bg-gradient-to-r from-slate-100 to-slate-50 border-b border-slate-200">
                    <tr>
                      <th 
                        className="w-[170px] lg:w-[200px] px-4 py-3 text-left text-sm font-semibold text-slate-900 cursor-pointer hover:bg-slate-200 transition-colors"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center gap-1">
                          Immobile
                          <ArrowUpDown className="h-3 w-3" />
                          {sortField === 'name' && (
                            <span className="text-xs text-blue-600">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="hidden lg:table-cell w-[100px] px-4 py-3 text-left text-sm font-semibold text-slate-900 cursor-pointer hover:bg-slate-200 transition-colors"
                        onClick={() => handleSort('code')}
                      >
                        <div className="flex items-center gap-1">
                          Codice
                          <ArrowUpDown className="h-3 w-3" />
                          {sortField === 'code' && (
                            <span className="text-xs text-blue-600">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="hidden xl:table-cell w-[140px] px-4 py-3 text-left text-sm font-semibold text-slate-900 cursor-pointer hover:bg-slate-200 transition-colors"
                        onClick={() => handleSort('city')}
                      >
                        <div className="flex items-center gap-1">
                          Località
                          <ArrowUpDown className="h-3 w-3" />
                          {sortField === 'city' && (
                            <span className="text-xs text-blue-600">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th className="hidden 2xl:table-cell w-[180px] px-4 py-3 text-center text-sm font-semibold text-slate-900">Caratteristiche</th>
                      <th 
                        className="hidden xl:table-cell w-[90px] px-4 py-3 text-center text-sm font-semibold text-slate-900 cursor-pointer hover:bg-slate-200 transition-colors"
                        onClick={() => handleSort('type')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Tipo
                          <ArrowUpDown className="h-3 w-3" />
                          {sortField === 'type' && (
                            <span className="text-xs text-blue-600">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th className="hidden lg:table-cell w-[80px] px-4 py-3 text-center text-sm font-semibold text-slate-900" title="Credenziali AlloggiatiWeb (Questura)">
                        <div className="flex items-center justify-center gap-1">
                          <Shield className="h-4 w-4 text-purple-600" />
                          <span className="hidden lg:inline">Questura</span>
                        </div>
                      </th>
                      <th className="hidden lg:table-cell w-[80px] px-4 py-3 text-center text-sm font-semibold text-slate-900" title="Dati ISTAT/ROSS1000 (automatico se CIR presente)">
                        <div className="flex items-center justify-center gap-1">
                          <Shield className="h-4 w-4" />
                          <span className="hidden lg:inline">ISTAT</span>
                        </div>
                      </th>
                      <th 
                        className="hidden lg:table-cell w-[130px] px-4 py-3 text-center text-sm font-semibold text-slate-900 cursor-pointer hover:bg-slate-200 transition-colors"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center justify-center gap-1">
                          Stato
                          <ArrowUpDown className="h-3 w-3" />
                          {sortField === 'status' && (
                            <span className="text-xs text-blue-600">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="hidden xl:table-cell w-[100px] px-4 py-3 text-center text-sm font-semibold text-slate-900 cursor-pointer hover:bg-slate-200 transition-colors"
                        onClick={() => handleSort('created')}
                        title="Data di pubblicazione (inserimento nel sistema)"
                        data-testid="properties-th-created"
                      >
                        <div className="flex items-center justify-center gap-1">
                          Pubblicata
                          <ArrowUpDown className="h-3 w-3" />
                          {sortField === 'created' && (
                            <span className="text-xs text-blue-600">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th className="w-[190px] lg:w-[420px] px-4 py-3 text-right text-sm font-semibold text-slate-900">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {paginatedProperties.map((property) => {
                      const isIncomplete = property.is_incomplete || false;
                      return (
                    <tr 
                      key={property.id} 
                      className={`hover:bg-slate-50 transition-colors ${
                        isIncomplete ? 'border-l-4 border-l-red-500 bg-red-50/30' : ''
                      }`}
                    >
                      <td className="px-4 py-3 align-top">
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                          <div className="font-medium text-slate-900 break-words">{property.name}</div>
                          {isIncomplete && (
                            <Badge variant="outline" className="text-xs bg-red-100 text-red-700 border-red-300">
                              ⚠ Dati Mancanti
                            </Badge>
                          )}
                        </div>
                        {isIncomplete && property.missing_fields && property.missing_fields.length > 0 && (
                          <div className="text-xs text-red-600 mt-1">
                            Mancanti: {property.missing_fields.slice(0, 3).join(', ')}
                            {property.missing_fields.length > 3 && ` +${property.missing_fields.length - 3}`}
                          </div>
                        )}
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3">
                        <Badge variant="outline" className="text-xs font-mono bg-blue-50 text-blue-700 border-blue-300">
                          {property.property_code || 'N/A'}
                        </Badge>
                      </td>
                      <td className="hidden xl:table-cell px-4 py-3">
                        <div className="flex items-center text-sm text-slate-600">
                          <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                          <span className="truncate">
                            {property.city}{property.province && ` (${property.province})`}
                          </span>
                        </div>
                      </td>
                      <td className="hidden 2xl:table-cell px-4 py-3 text-center">
                        <div className="flex items-center justify-center space-x-2 text-xs text-slate-600">
                          <span>{property.bedrooms === 0 ? 'Divano' : `${property.bedrooms} cam`}</span>
                          <span>•</span>
                          <span>{property.bathrooms} bagni</span>
                          <span>•</span>
                          <div className="flex items-center">
                            <Users className="h-3 w-3 mr-1" />
                            {property.max_guests}
                          </div>
                        </div>
                      </td>
                      <td className="hidden xl:table-cell px-4 py-3 text-center">
                        <span className="text-sm text-slate-600 capitalize">{property.property_type}</span>
                      </td>
                      {/* Colonna Credenziali Questura */}
                      <td className="hidden lg:table-cell px-4 py-3 text-center">
                        {property.has_questura_credentials ? (
                          <div 
                            className="flex items-center justify-center gap-1"
                            title={`Questura configurata${property.alloggiati_username ? ` - Username: ${property.alloggiati_username}` : ''}`}
                          >
                            <ShieldCheck className="h-5 w-5 text-green-600" />
                          </div>
                        ) : property.alloggiati_enabled ? (
                          <div 
                            className="flex items-center justify-center gap-1"
                            title="Questura abilitata ma credenziali incomplete"
                          >
                            <AlertTriangle className="h-5 w-5 text-yellow-500" />
                          </div>
                        ) : (
                          <div 
                            className="flex items-center justify-center gap-1"
                            title="Questura non configurata - Clicca Modifica per inserire le credenziali"
                          >
                            <ShieldOff className="h-5 w-5 text-slate-300" />
                          </div>
                        )}
                      </td>
                      {/* Colonna Delega ISTAT/ROSS1000 - Automatica basata su CIR */}
                      <td className="hidden lg:table-cell px-4 py-3 text-center">
                        {isRegionWithDelegation(property) ? (
                          <div className="flex items-center justify-center gap-1">
                            <div
                              className="h-7 px-2 flex items-center text-green-700"
                              title={`Dati ISTAT completi - Regione: ${getRegionFromCir(property.cir)?.name || 'N/A'}`}
                              data-testid={`istat-status-${property.id}`}
                            >
                              <ShieldCheck className="h-4 w-4" />
                            </div>
                            <span className="text-xs text-slate-400 hidden lg:inline">
                              {getRegionFromCir(property.cir)?.name?.substring(0, 3) || ''}
                            </span>
                          </div>
                        ) : (
                          <div 
                            className="flex items-center justify-center gap-1"
                            title="CIR mancante o regione non supportata"
                          >
                            <ShieldOff className="h-4 w-4 text-slate-300" />
                          </div>
                        )}
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3 text-center">
                        {(() => {
                          const eff = getEffectiveStatus(property);
                          const meta = STATUS_META[eff] || STATUS_META.active;
                          const scheduled = isScheduledTermination(property);
                          return (
                            <div className="flex flex-col items-center gap-1">
                              <Select
                                value={eff}
                                onValueChange={(val) => onSelectStatus(property, val)}
                                disabled={archivingProperty === property.id}
                              >
                                <SelectTrigger
                                  className={`h-7 w-[120px] mx-auto px-2 py-0 text-xs font-medium rounded-full border ${meta.trigger}`}
                                  data-testid={`status-select-${property.id}`}
                                >
                                  {archivingProperty === property.id
                                    ? <Loader2 className="h-3 w-3 animate-spin" />
                                    : <SelectValue />}
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="active" data-testid={`status-opt-active-${property.id}`}>Attivo</SelectItem>
                                  <SelectItem value="suspended" data-testid={`status-opt-suspended-${property.id}`}>Sospeso</SelectItem>
                                  <SelectItem value="terminated" data-testid={`status-opt-terminated-${property.id}`}>Cessato</SelectItem>
                                  <SelectItem value="archived" data-testid={`status-opt-archived-${property.id}`}>Archiviato</SelectItem>
                                  {scheduled && (
                                    <SelectItem value="cancel_schedule" data-testid={`status-opt-cancel-schedule-${property.id}`}>
                                      ↩ Annulla cessazione
                                    </SelectItem>
                                  )}
                                  {eff === 'private' && (
                                    <SelectItem value="private" disabled>Private</SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                              {scheduled && (
                                <span
                                  className="text-[10px] font-medium text-orange-600 leading-tight"
                                  data-testid={`scheduled-termination-${property.id}`}
                                  title={`Cessazione programmata il ${formatItDate(property.termination_date)}`}
                                >
                                  Cessa il {formatItDate(property.termination_date)}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="hidden xl:table-cell px-4 py-3 text-center text-sm text-slate-600 whitespace-nowrap" data-testid={`property-created-${property.id}`}>
                        {property.created_at ? formatItDate(property.created_at) : '—'}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex flex-wrap gap-1 justify-end">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEditProperty(property)}
                            data-testid={`modify-property-${property.id}`}
                            className="h-8"
                          >
                            <Edit2 className="h-3 w-3 mr-1" />
                            Modifica
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => handleViewDetails(property)}
                            data-testid={`details-property-${property.id}`}
                            className="h-8"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Dettagli
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.location.assign(`/properties/${property.id}/scheda`)}
                            data-testid={`scheda-property-${property.id}`}
                            className="h-8 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-indigo-200"
                            title="Scheda di pubblicazione: descrizione, foto, servizi, listini"
                          >
                            <FileText className="h-3 w-3 mr-1" />
                            Scheda
                          </Button>
                          {(user?.role === 'admin' || user?.role === 'customer_service') &&
                            getEffectiveStatus(property) === 'active' &&
                            (!property.has_questura_credentials || !isRegionWithDelegation(property)) && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200"
                              onClick={() => handleRequestCredentials(property)}
                              disabled={sendingCredsFor === property.id}
                              data-testid={`request-credentials-${property.id}`}
                              title="Invia al proprietario la richiesta credenziali Questura/ISTAT"
                            >
                              {sendingCredsFor === property.id
                                ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                : <Mail className="h-3 w-3 mr-1" />}
                              Richiedi credenziali
                            </Button>
                          )}
                          {property.property_code && property.status === 'active' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="h-8 bg-green-50 text-green-700 hover:bg-green-100"
                              onClick={() => handleOpenCalendar(property)}
                              data-testid={`associate-bookings-${property.id}`}
                            >
                              <Calendar className="h-3 w-3 mr-1" />
                              Associa
                            </Button>
                          )}
                          {isLiguriaProperty(property) && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="h-8 bg-green-50 text-green-700 hover:bg-green-100 border-green-300"
                              onClick={() => handleGenerateXml(property)}
                              data-testid={`generate-xml-${property.id}`}
                            >
                              <FileText className="h-3 w-3 mr-1" />
                              XML
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            {sortedProperties.length > 0 && (
              <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                    <div className="text-sm text-slate-600">
                      Mostrando {startIndex + 1} - {Math.min(endIndex, sortedProperties.length)} di {sortedProperties.length} immobili
                    </div>
                    <select 
                      value={itemsPerPage} 
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="text-sm border border-slate-300 rounded px-2 py-1"
                      data-testid="properties-items-per-page-select"
                    >
                      <option value={50}>50 per pagina</option>
                      <option value={100}>100 per pagina</option>
                      <option value={200}>200 per pagina</option>
                      <option value={500}>500 per pagina</option>
                      <option value={1000}>Tutti</option>
                    </select>
                  </div>
                  {totalPages > 1 && (
                  <div className="flex items-center justify-between gap-2 sm:justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="h-8 px-2 sm:px-3"
                      data-testid="properties-previous-page-button"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span className="hidden sm:inline">Precedente</span>
                    </Button>
                    <div className="hidden items-center gap-1 sm:flex" data-testid="properties-page-buttons">
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        // Show first page, last page, current page, and pages around current page
                        if (
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                        ) {
                          return (
                            <Button
                              key={pageNumber}
                              variant={currentPage === pageNumber ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(pageNumber)}
                              className={`h-8 w-8 ${currentPage === pageNumber ? 'bg-blue-900 text-white' : ''}`}
                              data-testid={`properties-page-${pageNumber}-button`}
                            >
                              {pageNumber}
                            </Button>
                          );
                        } else if (
                          pageNumber === currentPage - 2 ||
                          pageNumber === currentPage + 2
                        ) {
                          return <span key={pageNumber} className="px-1">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="h-8 px-2 sm:px-3"
                      data-testid="properties-next-page-button"
                    >
                      <span className="hidden sm:inline">Successiva</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                  )}
                </div>
              </div>
            )}
          </>
          )}
        </Card>
      </div>
      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettagli Immobile</DialogTitle>
          </DialogHeader>
          {selectedProperty && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Codice Immobile</Label>
                  <p className="font-mono font-semibold text-blue-700">{selectedProperty.property_code}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Nome</Label>
                  <p className="font-semibold">{selectedProperty.name}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Stato</Label>
                  <p>{selectedProperty.country}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Regione</Label>
                  <p>{selectedProperty.region}</p>
                </div>
                {selectedProperty.province && (
                  <div>
                    <Label className="text-xs text-gray-500">Provincia</Label>
                    <p>{selectedProperty.province}</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-gray-500">Città</Label>
                  <p>{selectedProperty.city}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">CAP</Label>
                  <p>{selectedProperty.postal_code}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-gray-500">Indirizzo</Label>
                  <p>{selectedProperty.address}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Tipo</Label>
                  <p className="capitalize">{selectedProperty.property_type}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Stato</Label>
                  <p>{getStatusBadge(selectedProperty.status)}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Camere</Label>
                  <p>{selectedProperty.bedrooms}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Bagni</Label>
                  <p>{selectedProperty.bathrooms}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Ospiti Max</Label>
                  <p>{selectedProperty.max_guests}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Costo Pulizia</Label>
                  <p>€{selectedProperty.cleaning_cost}</p>
                </div>
                {selectedProperty.police_station_code && (
                  <div>
                    <Label className="text-xs text-gray-500">Codice Questura</Label>
                    <p>{selectedProperty.police_station_code}</p>
                  </div>
                )}
                {selectedProperty.tourism_observatory && (
                  <div>
                    <Label className="text-xs text-gray-500">Osservatorio Turistico</Label>
                    <p>{selectedProperty.tourism_observatory}</p>
                  </div>
                )}
                {selectedProperty.partner_code && (
                  <div>
                    <Label className="text-xs text-gray-500">Partner</Label>
                    <p>{selectedProperty.partner_code}</p>
                  </div>
                )}
                {(selectedProperty.property_manager_id || selectedProperty.responsible_email) && (
                  <div>
                    <Label className="text-xs text-gray-500">Responsabile</Label>
                    <p>
                      {selectedProperty.responsible_email ? (
                        <span>{selectedProperty.responsible_email}</span>
                      ) : (
                        <span className="text-xs text-gray-400">ID: {selectedProperty.property_manager_id}</span>
                      )}
                    </p>
                  </div>
                )}
                {selectedProperty.booking_property_id && (
                  <div>
                    <Label className="text-xs text-gray-500">ID Booking.com</Label>
                    <p className="font-mono text-sm">{selectedProperty.booking_property_id}</p>
                  </div>
                )}
                {selectedProperty.airbnb_property_id && (
                  <div>
                    <Label className="text-xs text-gray-500">ID Airbnb</Label>
                    <p className="font-mono text-sm">{selectedProperty.airbnb_property_id}</p>
                  </div>
                )}
              </div>

              {/* Codici Identificativi */}
              <div className="pt-4 border-t">
                <h4 className="font-semibold text-slate-700 mb-3">📋 Codici Identificativi</h4>
                <div className="grid grid-cols-2 gap-4">
                  {selectedProperty.cin && (
                    <div>
                      <Label className="text-xs text-gray-500">CIN (Codice Identificativo Nazionale)</Label>
                      <p className="font-mono text-sm">{selectedProperty.cin}</p>
                    </div>
                  )}
                  {selectedProperty.cir && (
                    <div>
                      <Label className="text-xs text-gray-500">CIR (Codice Identificativo Regionale)</Label>
                      <p className="font-mono text-sm">{selectedProperty.cir}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Dati Proprietario */}
              {(selectedProperty.owner_name || selectedProperty.owner_email || selectedProperty.owner_iban) && (
                <div className="pt-4 border-t">
                  <h4 className="font-semibold text-slate-700 mb-3">Proprietario</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedProperty.owner_name && (
                      <div>
                        <Label className="text-xs text-gray-500">Nome</Label>
                        <p>{selectedProperty.owner_name}</p>
                      </div>
                    )}
                    {selectedProperty.owner_email && (
                      <div>
                        <Label className="text-xs text-gray-500">Email</Label>
                        <p className="text-sm">{selectedProperty.owner_email}</p>
                      </div>
                    )}
                    {selectedProperty.owner_iban && (
                      <div className="col-span-2">
                        <Label className="text-xs text-gray-500">IBAN</Label>
                        <p className="text-sm font-mono">{selectedProperty.owner_iban}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Budget e Last Minute */}
              <div className="pt-4 border-t">
                <h4 className="font-semibold text-slate-700 mb-3">💶 Budget & Last Minute</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Budget Interventi</Label>
                    <p className="font-semibold text-green-600">
                      {selectedProperty.intervention_budget ? `€ ${selectedProperty.intervention_budget}` : 'Non specificato'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Last Minute</Label>
                    <p>
                      {selectedProperty.last_minute_enabled ? (
                        <span className="text-green-600 font-semibold">✅ Abilitato</span>
                      ) : (
                        <span className="text-gray-400">❌ Disabilitato</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button onClick={() => setShowDetailsDialog(false)} className="w-full">
                  Chiudi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica Immobile</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            try {
              const token = localStorage.getItem('hw_token');
              // Build property data with only valid fields
              const propertyData = {
                property_code: newProperty.property_code || '',
                name: newProperty.name || '',
                address: newProperty.address || '',
                country: newProperty.country || 'Italia',
                region: newProperty.region || '',
                province: newProperty.province || '',
                city: newProperty.city || '',
                postal_code: newProperty.postal_code || '',
                property_type: newProperty.property_type || 'apartment',
                bedrooms: parseInt(newProperty.bedrooms) || 0,
                bathrooms: parseInt(newProperty.bathrooms) || 1,
                max_guests: parseInt(newProperty.max_guests) || 1,
                cleaning_cost: parseFloat(newProperty.cleaning_cost) || 0,
                linen_cost_per_person: parseFloat(newProperty.linen_cost_per_person) || 0,
                apply_submultiples: newProperty.apply_submultiples || false,
                available_submultiples: newProperty.available_submultiples || [],
                property_manager_id: newProperty.property_manager_id === "none" ? null : (newProperty.property_manager_id || null),
                partner_code: newProperty.partner_code === "none" ? null : (newProperty.partner_code || null),
                police_station_code: newProperty.police_station_code || '',
                tourism_observatory: newProperty.tourism_observatory || '',
                booking_property_id: newProperty.booking_property_id || '',
                airbnb_property_id: newProperty.airbnb_property_id || '',
                holidayworld_url: newProperty.holidayworld_url || '',
                cin: newProperty.cin || '',
                cir: newProperty.cir || '',
                intervention_budget: parseFloat(newProperty.intervention_budget) || 0,
                last_minute_enabled: newProperty.last_minute_enabled || false,
                last_minute_discount: parseFloat(newProperty.last_minute_discount) || 10,
                segnalatore_id: newProperty.segnalatore_id === "none" ? null : (newProperty.segnalatore_id || null),
                owner_name: newProperty.owner_name || '',
                owner_email: newProperty.owner_email || '',
                owner_iban: newProperty.owner_iban || '',
                owner_birth_date: newProperty.owner_birth_date || '',
                alloggiati_enabled: newProperty.alloggiati_enabled || false,
                alloggiati_username: newProperty.alloggiati_username || '',
                alloggiati_password: newProperty.alloggiati_password || '',
                alloggiati_wskey: newProperty.alloggiati_wskey || '',
                alloggiati_structure_code: newProperty.alloggiati_structure_code || '',
                alloggiati_apartment_id: newProperty.alloggiati_apartment_id || '',
                alloggiati_structure_type: newProperty.alloggiati_structure_type || '',
                alloggiati_beds_count: parseInt(newProperty.alloggiati_beds_count) || null,
                // ROSS1000 / ISTAT - CRITICAL: senza questi campi i dati non vengono salvati
                has_ross1000_delegation: newProperty.has_ross1000_delegation || false,
                ross1000_username: newProperty.ross1000_username || '',
                ross1000_password: newProperty.ross1000_password || '',
                ross1000_structure_code: newProperty.ross1000_structure_code || '',
                ross1000_cir_from_delegation: newProperty.ross1000_cir_from_delegation || '',
                ross1000_credentials_mode: newProperty.ross1000_credentials_mode || null,
                istat_comune_code: newProperty.istat_comune_code || '',
                // Autogestione Questura / ISTAT (proprietario)
                owner_self_manages_questura_istat: newProperty.owner_self_manages_questura_istat || false,
                // Modalità di pagamento al proprietario (PayPal vs Bonifico)
                owner_payment_method: newProperty.owner_payment_method || 'bank_transfer',
                owner_paypal_email: newProperty.owner_paypal_email || '',
                // Budget COSTS (sincronizzato da HW oppure override manuale)
                owner_annual_costs_budget: newProperty.owner_annual_costs_budget !== '' && newProperty.owner_annual_costs_budget != null
                  ? parseFloat(newProperty.owner_annual_costs_budget) : null,
                owner_daily_cost_incidence: newProperty.owner_daily_cost_incidence !== '' && newProperty.owner_daily_cost_incidence != null
                  ? parseFloat(newProperty.owner_daily_cost_incidence) : null,
                owner_costs_budget_year: newProperty.owner_costs_budget_year !== '' && newProperty.owner_costs_budget_year != null
                  ? parseInt(newProperty.owner_costs_budget_year) : null,
              };
              console.log('Updating property with data:', propertyData);
              await axios.put(`${API}/properties/${selectedProperty.id}`, propertyData, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (newProperty._mid_term_loaded) {
                try {
                  await axios.put(`${API}/properties/${selectedProperty.id}/publication`,
                    { mid_term: !!newProperty.mid_term, mid_term_note: newProperty.mid_term_note || '' },
                    { headers: { Authorization: `Bearer ${token}` } });
                } catch (mtErr) {
                  console.error('Errore salvataggio medio termine:', mtErr);
                  toast.error('Immobile salvato, ma errore nel salvataggio del Medio Termine');
                }
              }
              toast.success('Immobile aggiornato con successo!');
              setShowEditDialog(false);
              await fetchProperties(); // Await to ensure list is refreshed
            } catch (error) {
              console.error('Error updating property:', error);
              console.error('Error response:', error.response?.data);
              toast.error(`Errore durante l'aggiornamento: ${error.response?.data?.detail || error.message}`);
            }
          }} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Codice Immobile</Label>
                <Input
                  value={newProperty.property_code}
                  disabled
                  className="bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">Il codice non può essere modificato</p>
              </div>
              <div>
                <Label>Nome Immobile</Label>
                <Input
                  value={newProperty.name || ''}
                  onChange={(e) => setNewProperty({...newProperty, name: e.target.value})}
                  required
                />
              </div>
              <div className="col-span-2">
                <Label>Indirizzo</Label>
                <Input
                  value={newProperty.address || ''}
                  onChange={(e) => setNewProperty({...newProperty, address: e.target.value})}
                  placeholder="Via/Piazza..."
                />
              </div>
              <div>
                <Label>Stato</Label>
                <SearchableSelect
                  options={countries}
                  value={newProperty.country || 'Italia'}
                  onValueChange={handleCountryChange}
                  placeholder="Seleziona stato"
                  allowCustom={true}
                />
              </div>
              <div>
                <Label>Regione</Label>
                <SearchableSelect
                  options={regions}
                  value={newProperty.region || ''}
                  onValueChange={handleRegionChange}
                  placeholder="Seleziona regione"
                  allowCustom={true}
                />
              </div>
              <div>
                <Label>Provincia</Label>
                <Input
                  value={newProperty.province || ''}
                  onChange={(e) => setNewProperty({...newProperty, province: e.target.value.toUpperCase()})}
                  placeholder="Es: GE, MI, RM"
                  maxLength="2"
                  className="uppercase"
                />
              </div>
              <div>
                <Label>Città</Label>
                <SearchableSelect
                  options={cities}
                  value={newProperty.city || ''}
                  onValueChange={handleCityChange}
                  placeholder="Seleziona città"
                  allowCustom={true}
                />
              </div>
              <div>
                <Label>CAP</Label>
                <Input
                  value={newProperty.postal_code || ''}
                  onChange={(e) => setNewProperty({...newProperty, postal_code: e.target.value})}
                  placeholder="00000"
                />
              </div>
              <div>
                <Label>Tipo Immobile</Label>
                <Input
                  value={newProperty.property_type || 'apartment'}
                  onChange={(e) => setNewProperty({...newProperty, property_type: e.target.value})}
                  placeholder="Appartamento"
                />
              </div>
              <div>
                <Label>Camere</Label>
                <Input
                  type="number"
                  value={newProperty.bedrooms || 0}
                  onChange={(e) => setNewProperty({...newProperty, bedrooms: parseInt(e.target.value) || 0})}
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">0 = Solo divano letto</p>
              </div>
              <div>
                <Label>Bagni</Label>
                <Input
                  type="number"
                  value={newProperty.bathrooms || 1}
                  onChange={(e) => setNewProperty({...newProperty, bathrooms: parseInt(e.target.value) || 1})}
                  min="1"
                />
              </div>
              <div>
                <Label>Ospiti Max</Label>
                <Input
                  type="number"
                  value={newProperty.max_guests || 1}
                  onChange={(e) => setNewProperty({...newProperty, max_guests: parseInt(e.target.value) || 1})}
                  min="1"
                />
              </div>
              <div>
                <Label>Costo Pulizia (€)</Label>
                <Input
                  type="number"
                  value={newProperty.cleaning_cost || 0}
                  onChange={(e) => setNewProperty({...newProperty, cleaning_cost: parseFloat(e.target.value) || 0})}
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div>
                <Label>Costo Biancheria a Persona (€)</Label>
                <Input
                  type="number"
                  value={newProperty.linen_cost_per_person || 0}
                  onChange={(e) => setNewProperty({...newProperty, linen_cost_per_person: parseFloat(e.target.value) || 0})}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
                <p className="text-xs text-slate-500 mt-1">Costo per ospite (auto-moltiplicato nelle prenotazioni)</p>
              </div>

              
              {/* Sottomultipli Configuration */}
              <div className="col-span-2">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      id="edit_apply_submultiples"
                      checked={newProperty.apply_submultiples || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setNewProperty(prev => {
                          const maxGuests = parseInt(prev.max_guests) || 2;
                          let defaultSubs = [];
                          
                          if (checked) {
                            // Calculate default submultiples
                            defaultSubs.push(maxGuests);
                            if (maxGuests >= 4) defaultSubs.push(maxGuests - 2);
                            if (maxGuests >= 6) defaultSubs.push(maxGuests - 4);
                          }
                          
                          return {
                            ...prev,
                            apply_submultiples: checked,
                            available_submultiples: defaultSubs
                          };
                        });
                      }}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="edit_apply_submultiples" className="cursor-pointer font-semibold">
                      Applica Sottomultipli
                    </Label>
                  </div>
                  
                  {newProperty.apply_submultiples && (
                    <div className="space-y-3">
                      <p className="text-sm text-blue-900">
                        Posti letto disponibili per prenotazione:
                      </p>
                      
                      <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                        {Array.from({ length: parseInt(newProperty.max_guests) || 2 }, (_, i) => i + 1).map(num => {
                          const isChecked = (newProperty.available_submultiples || []).includes(num);
                          const isDefault = (
                            num === parseInt(newProperty.max_guests) ||
                            num === parseInt(newProperty.max_guests) - 2 ||
                            num === parseInt(newProperty.max_guests) - 4
                          );
                          
                          return (
                            <div key={num} className={`
                              p-2 border-2 rounded cursor-pointer text-center transition-all
                              ${isChecked 
                                ? 'border-blue-600 bg-blue-100' 
                                : 'border-slate-300 bg-white hover:border-blue-400'
                              }
                            `}>
                              <input
                                type="checkbox"
                                id={`edit_sub_${num}`}
                                checked={isChecked}
                                onChange={(e) => {
                                  setNewProperty(prev => ({
                                    ...prev,
                                    available_submultiples: e.target.checked
                                      ? [...(prev.available_submultiples || []), num].sort((a, b) => b - a)
                                      : (prev.available_submultiples || []).filter(n => n !== num)
                                  }));
                                }}
                                className="hidden"
                              />
                              <label htmlFor={`edit_sub_${num}`} className="cursor-pointer block">
                                <div className="font-bold text-lg">{num}</div>
                                {isDefault && <div className="text-xs text-blue-600">Default</div>}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Medio Termine (60+ notti) */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit_mid_term"
                    checked={!!newProperty.mid_term}
                    onChange={(e) => setNewProperty(prev => ({ ...prev, mid_term: e.target.checked }))}
                    disabled={!newProperty._mid_term_loaded}
                    className="w-4 h-4"
                    data-testid="edit-mid-term-checkbox"
                  />
                  <Label htmlFor="edit_mid_term" className="cursor-pointer font-semibold">
                    Medio termine (60+ notti)
                  </Label>
                  {!newProperty._mid_term_loaded && <span className="text-xs text-slate-400 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> caricamento...</span>}
                </div>
                <p className="text-xs text-indigo-900" data-testid="edit-mid-term-status">
                  {newProperty.mid_term ? 'Sì — applica il medio termine (listino dedicato)' : 'No — non applica il medio termine'}
                </p>
                {newProperty.mid_term && (
                  <div>
                    <Label>Condizioni medio termine (opzionale)</Label>
                    <Input
                      value={newProperty.mid_term_note || ''}
                      onChange={(e) => setNewProperty(prev => ({ ...prev, mid_term_note: e.target.value }))}
                      placeholder="Es. solo da Ott a Feb, max 6 mesi, su richiesta..."
                      data-testid="edit-mid-term-note"
                    />
                  </div>
                )}
              </div>

              
              {/* Partner & Manager */}
              <div>
                <Label>Partner Acquisitore</Label>
                <Select 
                  value={newProperty.partner_code || ""}
                  onValueChange={(value) => {
                    const stillValid = getSegnalatoriForPartner(value).some(s => s.segnalatore_id === newProperty.segnalatore_id);
                    setNewProperty({...newProperty, partner_code: value, segnalatore_id: stillValid ? newProperty.segnalatore_id : 'none'});
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona partner (opzionale)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun partner</SelectItem>
                    {partners.filter(p => p.partner_code).map(partner => (
                      <SelectItem key={partner.id} value={partner.partner_code || partner.id}>
                        {partner.name} ({partner.partner_code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Segnalatore di provenienza</Label>
                <SearchableSelect
                  options={[
                    { value: "none", label: "Nessun segnalatore" },
                    ...getSegnalatoriForPartner(newProperty.partner_code).map(s => ({
                      value: s.segnalatore_id,
                      label: `${s.nome || ''} ${s.cognome || ''}${s.email ? ` (${s.email})` : ''}`.trim()
                    }))
                  ]}
                  value={newProperty.segnalatore_id || "none"}
                  onValueChange={handleSegnalatoreChange}
                  placeholder="Seleziona segnalatore (opzionale)"
                  searchPlaceholder="Cerca segnalatore..."
                  allowCustom={false}
                  data-testid="segnalatore-select-edit"
                />
                <p className="text-xs text-slate-500 mt-1">🔗 HW Academy — Rete Segnalatori</p>
              </div>
              <div>
                <Label>Responsabile Gestione</Label>
                <SearchableSelect
                  options={[
                    { value: "none", label: "Nessun responsabile" },
                    { value: "self", label: "🏠 Proprietà (auto-gestito)" },
                    ...propertyManagers.filter(m => m.id && !m.archived).map(manager => ({
                      value: manager.id,
                      label: `${manager.first_name} ${manager.last_name}${manager.phone ? ` (${manager.phone})` : ''}`
                    }))
                  ]}
                  value={newProperty.property_manager_id || "none"}
                  onValueChange={(value) => setNewProperty({...newProperty, property_manager_id: value === "none" ? null : value})}
                  placeholder="Seleziona responsabile (opzionale)"
                  searchPlaceholder="Cerca responsabile..."
                  allowCustom={false}
                />
                <p className="text-xs text-slate-500 mt-1">
                  👨‍💼 Cerca responsabile per nome o telefono
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => setShowManagerHistory(selectedProperty?.id || null)}
                  data-testid="show-manager-history-btn"
                >
                  🕘 Storico responsabili
                </Button>
              </div>
              
              {/* Codice Questura e Osservatorio */}
              <div>
                <Label>Codice Questura (Alloggiatiweb)</Label>
                <Input
                  value={newProperty.police_station_code || ''}
                  onChange={(e) => setNewProperty({...newProperty, police_station_code: e.target.value})}
                  placeholder="es. 001234"
                />
              </div>
              <div>
                <Label>Osservatorio Turistico</Label>
                <Input
                  value={newProperty.tourism_observatory || ''}
                  onChange={(e) => setNewProperty({...newProperty, tourism_observatory: e.target.value})}
                  placeholder="es. Osservatorio Lombardia"
                />
              </div>
              
              {/* Platform Property IDs */}
              <div>
                <Label>ID Proprietà Booking.com</Label>
                <Input
                  type="text"
                  value={newProperty.booking_property_id || ''}
                  onChange={(e) => setNewProperty({...newProperty, booking_property_id: e.target.value})}
                  placeholder="es: 1234567"
                />
                <p className="text-xs text-slate-500 mt-1">
                  🔑 <strong>Importante:</strong> ID dalla extranet Booking.com
                </p>
              </div>
              <div>
                <Label>ID Proprietà Airbnb</Label>
                <Input
                  type="text"
                  value={newProperty.airbnb_property_id || ''}
                  onChange={(e) => setNewProperty({...newProperty, airbnb_property_id: e.target.value})}
                  placeholder="es: 98765432"
                />
                <p className="text-xs text-slate-500 mt-1">
                  🔑 <strong>Importante:</strong> ID dalla dashboard Airbnb
                </p>
              </div>
              <div className="col-span-2">
                <Label>Link casa su holidayworld.it</Label>
                <Input
                  type="url"
                  value={newProperty.holidayworld_url || ''}
                  onChange={(e) => setNewProperty({...newProperty, holidayworld_url: e.target.value})}
                  placeholder="https://www.holidayworld.it/..."
                  data-testid="edit-holidayworld-url-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  📬 Usato come destinazione del pulsante nelle <strong>newsletter</strong>
                </p>
              </div>
            </div>

            {/* Codici Identificativi e Proprietario - EDIT */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold text-slate-700 mb-3">📋 Codici Identificativi & Proprietario</h4>
              <div className="mb-3 p-2 bg-slate-50 rounded-md space-y-1" data-testid="edit-property-dates-general">
                <CredDate label="Creazione immobile" value={selectedProperty?.created_at} showBadge={false} />
                <CredDate label="Cessazione" value={selectedProperty?.termination_date} showBadge={false} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>CIN</Label>
                  <Input
                    type="text"
                    value={newProperty.cin || ''}
                    onChange={(e) => setNewProperty({...newProperty, cin: e.target.value.toUpperCase()})}
                    placeholder="IT069050C25M8THUMX"
                    className="uppercase"
                  />
                </div>
                <div>
                  <Label>CIR</Label>
                  <Input
                    type="text"
                    value={newProperty.cir || ''}
                    onChange={(e) => {
                      const cir = e.target.value.toUpperCase();
                      const oldCir = (newProperty.cir || '').toUpperCase();
                      const updates = { cir };
                      // Auto-sync CIR da Delega se vuoto o se uguale al vecchio CIR (non modificato manualmente)
                      const currentDelegaCir = (newProperty.ross1000_cir_from_delegation || '').toUpperCase();
                      if (!currentDelegaCir || currentDelegaCir === oldCir) {
                        updates.ross1000_cir_from_delegation = cir;
                      }
                      setNewProperty({ ...newProperty, ...updates });
                    }}
                    placeholder="069074CVP0031"
                    className="uppercase"
                  />
                </div>
                <div>
                  <Label>Nome Proprietario</Label>
                  <Input
                    type="text"
                    value={newProperty.owner_name || ''}
                    onChange={(e) => setNewProperty({...newProperty, owner_name: e.target.value})}
                    placeholder="Mario Rossi"
                  />
                </div>
                <div>
                  <Label>Email Proprietario</Label>
                  <Input
                    type="email"
                    value={newProperty.owner_email || ''}
                    onChange={(e) => setNewProperty({...newProperty, owner_email: e.target.value})}
                    placeholder="proprietario@email.com"
                  />
                </div>
                <div>
                  <Label>Data di nascita Proprietario</Label>
                  <Input
                    data-testid="owner-birth-date-edit"
                    type="date"
                    value={newProperty.owner_birth_date || ''}
                    onChange={(e) => setNewProperty({...newProperty, owner_birth_date: e.target.value})}
                  />
                  <p className="text-xs text-slate-500 mt-1">Per gli auguri di compleanno automatici</p>
                </div>
                <div className="col-span-2">
                  <Label>IBAN Proprietario</Label>
                  <Input
                    data-testid="owner-iban-input"
                    type="text"
                    value={newProperty.owner_iban || ''}
                    onChange={(e) => setNewProperty({...newProperty, owner_iban: e.target.value.toUpperCase().replace(/\s/g, '')})}
                    placeholder="IT60X0542811101000000123456"
                    maxLength={34}
                  />
                </div>
              </div>
            </div>

            {/* Modalità pagamento al proprietario - EDIT */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold text-slate-700 mb-3">💳 Modalità pagamento al proprietario</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Modalità</Label>
                  <select
                    data-testid="owner-payment-method-edit"
                    value={newProperty.owner_payment_method || 'bank_transfer'}
                    onChange={(e) => setNewProperty({...newProperty, owner_payment_method: e.target.value})}
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm bg-white"
                  >
                    <option value="bank_transfer">Bonifico SEPA (Holiday World)</option>
                    <option value="paypal">PayPal (gestito da HW Smart)</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    {(newProperty.owner_payment_method || 'bank_transfer') === 'paypal'
                      ? 'I pagamenti compariranno nella sezione "PayPal da pagare"'
                      : 'Gestito dal sistema bonifici di Holiday World (default)'}
                  </p>
                </div>
              {newProperty.owner_payment_method === 'paypal' && (
                <>
                  <div className="md:col-span-2">
                    <Label>Account PayPal proprietario</Label>
                    <Input
                      data-testid="owner-paypal-email-edit"
                      type="text"
                      value={newProperty.owner_paypal_email || ''}
                      onChange={(e) => setNewProperty({...newProperty, owner_paypal_email: e.target.value})}
                      placeholder="proprietario@paypal.com oppure @handle"
                    />
                    <p className="text-xs text-slate-500 mt-1">Email, telefono o handle (es. @nome) dell'account PayPal del proprietario. Può essere completato in seguito quando lo verifichi.</p>
                  </div>
                  <div>
                    <Label>Budget COSTS annuo (€)</Label>
                    <Input
                      data-testid="owner-annual-costs-budget"
                      type="number" step="0.01" min="0"
                      value={newProperty.owner_annual_costs_budget ?? ''}
                      onChange={(e) => setNewProperty({...newProperty, owner_annual_costs_budget: e.target.value === '' ? null : parseFloat(e.target.value)})}
                      placeholder="3500.00"
                    />
                    <p className="text-xs text-slate-500 mt-1">Importo annuale spese che gravano sull'immobile. Modificabile manualmente; auto-aggiornato anche da HW Sync. Ogni modifica è tracciata nello storico.</p>
                  </div>
                  <div>
                    <Label>Coefficiente €/giorno</Label>
                    <Input
                      data-testid="owner-daily-cost-incidence"
                      type="number" step="0.01" min="0"
                      value={newProperty.owner_daily_cost_incidence ?? ''}
                      onChange={(e) => setNewProperty({...newProperty, owner_daily_cost_incidence: e.target.value === '' ? null : parseFloat(e.target.value)})}
                      placeholder="50.00"
                    />
                    <p className="text-xs text-slate-500 mt-1">Incidenza giornaliera dei costi (es. €50/giorno). Usato per calcolare quota COST nei pagamenti PayPal.</p>
                  </div>
                  <div>
                    <Label>Anno budget</Label>
                    <Input
                      data-testid="owner-costs-budget-year"
                      type="number" step="1" min="2020" max="2099"
                      value={newProperty.owner_costs_budget_year ?? ''}
                      onChange={(e) => setNewProperty({...newProperty, owner_costs_budget_year: e.target.value === '' ? null : parseInt(e.target.value)})}
                      placeholder={String(new Date().getFullYear())}
                    />
                    <p className="text-xs text-slate-500 mt-1">Anno solare di riferimento del budget. Il sistema calcola il carry-over al cambio anno.</p>
                  </div>
                  <div className="md:col-span-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setShowBudgetHistory(selectedProperty?.id || null)}
                      data-testid="show-budget-history-btn"
                    >
                      📜 Storico modifiche budget COSTS
                    </Button>
                  </div>
                </>
              )}
              </div>
            </div>

            {/* Budget e Last Minute - EDIT */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold text-slate-700 mb-3">💶 Budget & Last Minute</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Budget Interventi (€)</Label>
                  <Input
                    type="number"
                    value={newProperty.intervention_budget || ''}
                    onChange={(e) => setNewProperty({...newProperty, intervention_budget: parseFloat(e.target.value) || 0})}
                    placeholder="0"
                    min="0"
                    step="50"
                  />
                </div>
                <div>
                  <Label>Last Minute</Label>
                  <div className="flex items-center space-x-2 mt-2">
                    <input
                      type="checkbox"
                      checked={newProperty.last_minute_enabled || false}
                      onChange={(e) => setNewProperty({...newProperty, last_minute_enabled: e.target.checked})}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="text-sm">
                      {newProperty.last_minute_enabled ? '✅' : '❌'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* AlloggiatiWeb Integration - EDIT */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold text-slate-700 mb-3">🏛️ Integrazione AlloggiatiWeb (Questura)</h4>
              {(selectedProperty?.alloggiati_credentials_set_at || selectedProperty?.alloggiati_first_submission_at) && (
                <div className="mb-3 p-2 bg-slate-50 rounded-md space-y-1" data-testid="edit-questura-dates">
                  <CredDate label="Credenziali Questura inserite" value={selectedProperty?.alloggiati_credentials_set_at} estimated={selectedProperty?.alloggiati_credentials_set_at_estimated} />
                  <CredDate label="Primo invio Questura" value={selectedProperty?.alloggiati_first_submission_at} showBadge={false} />
                  <CredDate label="Ultima modifica credenziali" value={selectedProperty?.alloggiati_credentials_updated_at} showBadge={false} />
                </div>
              )}
              <div className="bg-blue-50 p-3 rounded-lg mb-4">
                <p className="text-sm text-blue-700">
                  Configura le credenziali per l'invio automatico delle schedine alloggiati alla Questura.
                </p>
              </div>
              
              <div className="flex items-center space-x-2 mb-4">
                <input
                  type="checkbox"
                  checked={newProperty.alloggiati_enabled || false}
                  onChange={(e) => setNewProperty({...newProperty, alloggiati_enabled: e.target.checked})}
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <span className="text-sm font-medium text-slate-700">
                  {newProperty.alloggiati_enabled ? '✅ AlloggiatiWeb Abilitato' : '❌ AlloggiatiWeb Disabilitato'}
                </span>
              </div>
              
              {newProperty.alloggiati_enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Username Portale</Label>
                    <Input
                      type="text"
                      value={newProperty.alloggiati_username || ''}
                      onChange={(e) => {
                        const username = e.target.value;
                        const oldUsername = newProperty.alloggiati_username || '';
                        // Auto-aggiorna codice struttura e ID appartamento se:
                        // 1. Sono vuoti, oppure
                        // 2. Sono uguali al vecchio username (non modificati manualmente)
                        const updates = { alloggiati_username: username };
                        if (!newProperty.alloggiati_structure_code || newProperty.alloggiati_structure_code === oldUsername) {
                          updates.alloggiati_structure_code = username;
                        }
                        if (!newProperty.alloggiati_apartment_id || newProperty.alloggiati_apartment_id === oldUsername) {
                          updates.alloggiati_apartment_id = username;
                        }
                        setNewProperty({...newProperty, ...updates});
                      }}
                      placeholder="Username Questura"
                      name="alloggiati-user-nofill"
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <Label>Password Portale</Label>
                    <div className="relative">
                      <Input
                        type={showPassword.alloggiati_password ? "text" : "password"}
                        value={newProperty.alloggiati_password || ''}
                        onChange={(e) => setNewProperty({...newProperty, alloggiati_password: e.target.value, })}
                        placeholder="••••••••"
                        name="alloggiati-pass-nofill"
                        autoComplete="new-password"
                        className="pr-10"
                        data-testid="edit-alloggiati-password-input"
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 disabled:opacity-50"
                        onClick={handleRevealCreds}
                        disabled={revealingCreds}
                        title={credsRevealed ? "Mostra/nascondi" : "Mostra password in chiaro (decifra)"}
                        data-testid="reveal-credentials-btn"
                      >
                        {revealingCreds
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : (showPassword.alloggiati_password ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />)}
                      </button>
                    </div>
                    {!credsRevealed && String(newProperty.alloggiati_password || '').startsWith('enc::v1::') && (
                      <p className="text-xs text-slate-500 mt-1">🔒 Cifrata — clicca l'occhio per vederla in chiaro</p>
                    )}
                  </div>
                  <div>
                    <Label>Web Service Key (WSKEY)</Label>
                    <Input
                      type={showPassword.alloggiati_password ? "text" : "password"}
                      value={newProperty.alloggiati_wskey || ''}
                      onChange={(e) => setNewProperty({...newProperty, alloggiati_wskey: e.target.value})}
                      placeholder="Chiave generata dal portale"
                      autoComplete="new-password"
                      data-testid="edit-alloggiati-wskey-input"
                    />
                    {!credsRevealed && String(newProperty.alloggiati_wskey || '').startsWith('enc::v1::') && (
                      <p className="text-xs text-slate-500 mt-1">🔒 Cifrata — clicca l'occhio sulla password per vederla in chiaro</p>
                    )}
                  </div>
                  <div>
                    <Label>Codice Struttura</Label>
                    <Input
                      type="text"
                      value={newProperty.alloggiati_structure_code || ''}
                      onChange={(e) => setNewProperty({...newProperty, alloggiati_structure_code: e.target.value})}
                      placeholder="Codice assegnato dalla Questura"
                    />
                  </div>
                  <div>
                    <Label>Tipo Struttura</Label>
                    <Select
                      value={newProperty.alloggiati_structure_type || ''}
                      onValueChange={(value) => setNewProperty({...newProperty, alloggiati_structure_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="albergo">Albergo</SelectItem>
                        <SelectItem value="bb">B&B</SelectItem>
                        <SelectItem value="affittacamere">Affittacamere</SelectItem>
                        <SelectItem value="casa_vacanze">Casa Vacanze</SelectItem>
                        <SelectItem value="appartamento">Appartamento</SelectItem>
                        <SelectItem value="residence">Residence</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>ID Appartamento</Label>
                    <Input
                      type="text"
                      value={newProperty.alloggiati_apartment_id || ''}
                      onChange={(e) => setNewProperty({...newProperty, alloggiati_apartment_id: e.target.value.toUpperCase()})}
                      placeholder="Es: GE0459678"
                    />
                  </div>
                  <div>
                    <Label>Posti Letto Registrati</Label>
                    <Input
                      type="number"
                      value={newProperty.alloggiati_beds_count || ''}
                      onChange={(e) => setNewProperty({...newProperty, alloggiati_beds_count: parseInt(e.target.value) || ''})}
                      placeholder="Numero posti letto"
                      min="1"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* AUTOGESTIONE Q/ISTAT - flag proprietario gestisce in proprio */}
            <div className="border-t pt-4 mt-4" data-testid="edit-self-manage-section">
              <h4 className="font-semibold text-slate-700 mb-3">👤 Autogestione Questura / ISTAT</h4>
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg mb-3">
                <p className="text-xs text-amber-900 leading-relaxed">
                  Spunta questa casella se <b>il proprietario gestisce in autonomia</b> Questura (Alloggiati Web)
                  e ISTAT/ROSS1000 per questo immobile. <b>Attivando il flag verrà inviata automaticamente
                  un'email al proprietario</b> con l'offerta del servizio automatizzato HW Smart e il magic link
                  al portale per inserire le credenziali (invio una sola volta per immobile).
                </p>
              </div>
              <label className="flex items-start gap-2 cursor-pointer p-3 border rounded-lg hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={!!newProperty.owner_self_manages_questura_istat}
                  onChange={(e) => setNewProperty({...newProperty, owner_self_manages_questura_istat: e.target.checked})}
                  className="mt-1 h-4 w-4 text-amber-600 rounded"
                  data-testid="edit-owner-self-manages"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm text-slate-800">
                    Il proprietario gestisce in autonomia Questura e ISTAT
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Email proprietario: <code className="bg-slate-100 px-1 rounded">{newProperty.owner_email || '— manca —'}</code>
                  </div>
                </div>
              </label>
            </div>

            {/* ISTAT / ROSS1000 Integration - EDIT */}
            <div className="border-t pt-4 mt-4" data-testid="edit-ross1000-section">
              <h4 className="font-semibold text-slate-700 mb-3">🗺️ Integrazione ISTAT / ROSS1000 (Flussi Turistici)</h4>
              {(selectedProperty?.ross1000_credentials_set_at || selectedProperty?.ross1000_first_submission_at || selectedProperty?.ross1000_lock_until) && (
                <div className="mb-3 p-2 bg-slate-50 rounded-md space-y-1" data-testid="edit-ross1000-dates">
                  <CredDate label="Credenziali/Delega ROSS1000 inserite" value={selectedProperty?.ross1000_credentials_set_at} estimated={selectedProperty?.ross1000_credentials_set_at_estimated} />
                  <CredDate label="Primo invio ROSS1000" value={selectedProperty?.ross1000_first_submission_at} showBadge={false} />
                  {selectedProperty?.ross1000_lock_until && (
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <span className="font-medium text-slate-500">Periodo associazione (da):</span>
                      <span className="text-amber-700 font-medium">{selectedProperty.ross1000_lock_until}</span>
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-700">date precedenti bloccate dal portale</span>
                    </div>
                  )}
                </div>
              )}
              <div className="bg-emerald-50 p-3 rounded-lg mb-4">
                <p className="text-sm text-emerald-700">
                  Configura le credenziali per l'invio automatico dei flussi turistici al portale regionale
                  (ROSS1000 / GIES / Osservatori). Queste credenziali sono <strong>indipendenti</strong> da quelle della Questura.
                </p>
              </div>

              <div className="flex items-center space-x-2 mb-4">
                <input
                  type="checkbox"
                  checked={newProperty.has_ross1000_delegation || false}
                  onChange={(e) => setNewProperty({...newProperty, has_ross1000_delegation: e.target.checked})}
                  className="h-4 w-4 text-emerald-600 rounded"
                  data-testid="edit-ross1000-enabled"
                />
                <span className="text-sm font-medium text-slate-700">
                  {newProperty.has_ross1000_delegation ? '✅ Delega ROSS1000/ISTAT Attiva' : '❌ Nessuna Delega ROSS1000/ISTAT'}
                </span>
              </div>

              {newProperty.has_ross1000_delegation && (() => {
                const groupCreds = getGroupCredsForProvince(newProperty.province);
                const mode = newProperty.ross1000_credentials_mode || (groupCreds ? 'group' : 'individual');
                const effectiveUsername = mode === 'group' && groupCreds ? groupCreds.username : (newProperty.ross1000_username || '');
                const effectivePassword = mode === 'group' && groupCreds ? groupCreds.password : (newProperty.ross1000_password || '');
                const isSicilia = (newProperty.region || '').trim().toLowerCase() === 'sicilia';
                return (
                  <>
                    {/* Banner Sicilia OTRS */}
                    {isSicilia && (
                      <div className="mb-4 p-3 bg-orange-50 border border-orange-300 rounded-lg" data-testid="sicilia-otrs-banner">
                        <p className="text-sm font-semibold text-orange-900 mb-1">
                          🇮🇹 Sicilia → portale OTRS (NON ROSS1000)
                        </p>
                        <p className="text-xs text-orange-800">
                          Per la Sicilia il sistema invia i dati al portale dedicato{' '}
                          <a
                            href="https://osservatorioturistico.regione.sicilia.it"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline font-medium"
                          >
                            Osservatorio Turistico Regione Siciliana (OTRS)
                          </a>.
                          L'<strong>Username</strong> qui sotto è il <strong>HotelCode rilasciato dall'Osservatorio</strong> (formato <code className="bg-orange-100 px-1 rounded">TRS-IT-SIC-XXXXX</code>),
                          assegnato al singolo immobile dopo accreditamento via PEC a{' '}
                          <code className="bg-orange-100 px-1 rounded text-[11px]">servizioturistico.ct@certmail.regione.sicilia.it</code>.
                          Il "Codice Struttura" coincide con l'username e non serve compilarlo.
                        </p>
                      </div>
                    )}

                    {/* Banner Campania Sinfonia */}
                    {(newProperty.region || '').trim().toLowerCase() === 'campania' && (
                      <div className="mb-4 p-3 bg-sky-50 border border-sky-300 rounded-lg" data-testid="campania-sinfonia-banner">
                        <p className="text-sm font-semibold text-sky-900 mb-1">
                          🇮🇹 Campania → Sinfonia Turismo SMART (NON ROSS1000)
                        </p>
                        <p className="text-xs text-sky-800">
                          Per la Campania il sistema invia i dati aggregati (C/59) al portale{' '}
                          <a
                            href="https://turismo.regione.campania.it/turismoweb/fe-portale-turismo/login"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline font-medium"
                          >
                            Sinfonia Turismo SMART
                          </a>.
                          Nel campo <strong>Username</strong> inserisci il <strong>CUSR</strong> (Codice Unico Struttura Ricettiva, es. <code className="bg-sky-100 px-1 rounded">15123123ALBXXXX</code>)
                          e nel campo <strong>Password</strong> l'<strong>API KEY</strong> della struttura.
                          Entrambi si recuperano accedendo con SPID al portale Sinfonia: CUSR in "Profilo Utente → Visualizza strutture ricettive",
                          API KEY in "Anagrafica → Gestione Anagrafica Struttura Ricettiva".
                        </p>
                      </div>
                    )}

                    {/* Tipo credenziali: Gruppo vs Individuali */}
                    <div className="mb-4 bg-white border rounded-lg p-3">
                      <Label className="mb-2 block">Tipo Credenziali ROSS1000</Label>
                      <div className="flex flex-col md:flex-row gap-3">
                        <label className={`flex-1 flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition ${mode === 'group' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'} ${!groupCreds ? 'opacity-50 cursor-not-allowed' : ''}`}>
                          <input
                            type="radio"
                            name="edit-ross1000-mode"
                            value="group"
                            checked={mode === 'group'}
                            disabled={!groupCreds}
                            onChange={() => setNewProperty({...newProperty, ross1000_credentials_mode: 'group'})}
                            className="mt-1"
                            data-testid="edit-ross1000-mode-group"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm">👥 Account di Gruppo (provincia)</div>
                            <div className="text-xs text-slate-500 mt-1">
                              {groupCreds
                                ? `Usa le credenziali condivise per ${groupCreds.province} (${groupCreds.region}). User/password pre-compilati automaticamente.`
                                : (
                                  <span>
                                    Nessun account di gruppo disponibile per la provincia "{newProperty.province || '-'}".{' '}
                                    <button
                                      type="button"
                                      className="text-blue-600 hover:text-blue-800 underline font-medium"
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openQuickGroupDialog(); }}
                                      data-testid="quick-create-group-cred-btn-edit"
                                    >
                                      ➕ Crealo adesso
                                    </button>
                                  </span>
                                )}
                            </div>
                          </div>
                        </label>
                        <label className={`flex-1 flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition ${mode === 'individual' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                          <input
                            type="radio"
                            name="edit-ross1000-mode"
                            value="individual"
                            checked={mode === 'individual'}
                            onChange={() => setNewProperty({...newProperty, ross1000_credentials_mode: 'individual'})}
                            className="mt-1"
                            data-testid="edit-ross1000-mode-individual"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm">🔑 Credenziali Individuali</div>
                            <div className="text-xs text-slate-500 mt-1">
                              Il proprietario ha un proprio account ROSS1000. Inserisci user/password manualmente.
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Username ROSS1000 / GIES</Label>
                        <Input
                          type="text"
                          value={effectiveUsername}
                          onChange={(e) => setNewProperty({...newProperty, ross1000_username: e.target.value})}
                          placeholder="Username portale regionale"
                          name="ross1000-user-nofill"
                          autoComplete="off"
                          data-testid="edit-ross1000-username"
                          readOnly={mode === 'group'}
                          className={mode === 'group' ? 'bg-slate-100 cursor-not-allowed' : ''}
                        />
                        {mode === 'group' && groupCreds && (
                          <p className="text-xs text-emerald-700 mt-1">
                            🔒 Da account di gruppo <strong>{groupCreds.province}</strong>
                          </p>
                        )}
                      </div>
                      <div>
                        <Label>Password ROSS1000 / GIES</Label>
                        <div className="relative">
                          <Input
                            type={showPassword.ross1000_password ? "text" : "password"}
                            value={effectivePassword}
                            onChange={(e) => setNewProperty({...newProperty, ross1000_password: e.target.value})}
                            placeholder="••••••••"
                            name="ross1000-pass-nofill"
                            autoComplete="new-password"
                            className={`pr-10 ${mode === 'group' ? 'bg-slate-100 cursor-not-allowed' : ''}`}
                            data-testid="edit-ross1000-password"
                            readOnly={mode === 'group'}
                          />
                          <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            onClick={() => setShowPassword(prev => ({ ...prev, ross1000_password: !prev.ross1000_password }))}
                            data-testid="toggle-ross1000-password-edit"
                          >
                            {showPassword.ross1000_password ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        {mode === 'group' && groupCreds && (
                          <p className="text-xs text-emerald-700 mt-1">
                            🔒 Da account di gruppo <strong>{groupCreds.province}</strong>
                          </p>
                        )}
                      </div>
                      <div>
                        <Label>Codice Struttura / ID Immobile ROSS1000</Label>
                        <Input
                          type="text"
                          value={newProperty.ross1000_structure_code || ''}
                          onChange={(e) => setNewProperty({...newProperty, ross1000_structure_code: e.target.value})}
                          placeholder="Lascia vuoto se non assegnato"
                          data-testid="edit-ross1000-structure-code"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Se vuoto verrà considerato <code>0</code> e non verrà inviato al portale
                        </p>
                      </div>
                      <div>
                        <Label>CIR da Delega</Label>
                        <Input
                          type="text"
                          value={newProperty.ross1000_cir_from_delegation || ''}
                          onChange={(e) => setNewProperty({...newProperty, ross1000_cir_from_delegation: e.target.value.toUpperCase()})}
                          placeholder="CIR registrato nel file delega"
                          className="uppercase"
                          data-testid="edit-ross1000-cir"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Copiato automaticamente dal CIR sopra, modificabile solo se diverso dal file delega regionale
                        </p>
                      </div>
                      <div className="md:col-span-2">
                        <Label>Codice Comune ISTAT (auto)</Label>
                        <Input
                          type="text"
                          value={newProperty.istat_comune_code || ''}
                          onChange={(e) => setNewProperty({...newProperty, istat_comune_code: e.target.value})}
                          placeholder="Auto-calcolato da città/provincia"
                          data-testid="edit-istat-comune-code"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Di norma calcolato automaticamente; modificabile solo se necessario.
                        </p>
                      </div>
                    </div>

                    {/* === BACKFILL ROSS1000: invio retroattivo === */}
                    {selectedProperty?.id && (
                      <Ross1000BackfillSection
                        propertyId={selectedProperty.id}
                        propertyName={selectedProperty.name || newProperty.name}
                      />
                    )}
                  </>
                );
              })()}
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">
                Annulla
              </Button>
              <Button type="submit" className="flex-1">
                Salva Modifiche
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inline Status Change Dialog (dalla tendina nella colonna Stato) */}
      <Dialog open={statusChange.open} onOpenChange={(o) => { if (!o) closeStatusChange(); }}>
        <DialogContent className="max-w-md" data-testid="status-change-dialog">
          <DialogHeader>
            <DialogTitle>
              {statusChange.target === 'active' && 'Riattiva immobile'}
              {statusChange.target === 'suspended' && 'Sospendi immobile'}
              {statusChange.target === 'archived' && 'Archivia immobile'}
              {statusChange.target === 'terminated' && 'Cessa immobile'}
              {statusChange.target === 'cancel_schedule' && 'Annulla cessazione programmata'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              {statusChange.property?.name}
              {statusChange.property?.property_code ? ` · ${statusChange.property.property_code}` : ''}
            </p>

            {statusChange.target === 'active' && (
              <p className="text-sm">L'immobile tornerà <strong>Attivo</strong> e rientrerà in tutti i processi.</p>
            )}

            {statusChange.target === 'cancel_schedule' && (
              <p className="text-sm">
                La cessazione programmata
                {statusChange.property?.termination_date ? ` del ${formatItDate(statusChange.property.termination_date)}` : ''}
                {' '}verrà annullata. L'immobile resterà <strong>Attivo</strong> senza data di cessazione.
              </p>
            )}

            {(statusChange.target === 'suspended' || statusChange.target === 'archived') && (
              <div className="space-y-2">
                <Label htmlFor="status-change-reason">Motivo <span className="text-slate-400">(facoltativo)</span></Label>
                <Input
                  id="status-change-reason"
                  data-testid="status-change-reason-input"
                  value={statusChangeReason}
                  onChange={(e) => setStatusChangeReason(e.target.value)}
                  placeholder={statusChange.target === 'suspended' ? 'Es: Lavori in corso, problema temporaneo…' : 'Es: Contratto terminato, immobile venduto…'}
                />
                <p className="text-xs text-slate-500">
                  {statusChange.target === 'suspended'
                    ? "L'immobile verrà escluso temporaneamente dai processi attivi."
                    : "L'immobile verrà archiviato (non operativo)."}
                </p>
              </div>
            )}

            {statusChange.target === 'terminated' && (() => {
              const todayStr = new Date().toISOString().slice(0, 10);
              const isFuture = statusChangeDate && statusChangeDate > todayStr;
              return (
                <div className="space-y-2">
                  <Label htmlFor="status-change-date">Data di cessazione <span className="text-red-500">*</span></Label>
                  <Input
                    id="status-change-date"
                    type="date"
                    data-testid="status-change-date-input"
                    value={statusChangeDate}
                    onChange={(e) => setStatusChangeDate(e.target.value)}
                  />
                  {!statusChangeDate && (
                    <p className="text-xs text-slate-500">Con data <strong>futura</strong> l'immobile resta Attivo fino a quel giorno; con data di oggi diventa subito Cessato.</p>
                  )}
                  {statusChangeDate && isFuture && (
                    <p className="text-xs text-orange-600" data-testid="status-change-future-hint">
                      ✅ Cessazione <strong>programmata</strong>: l'immobile resta <strong>Attivo e operativo</strong> fino al {formatItDate(statusChangeDate)}, poi diventerà Cessato automaticamente.
                    </p>
                  )}
                  {statusChangeDate && !isFuture && (
                    <p className="text-xs text-gray-600" data-testid="status-change-immediate-hint">
                      L'immobile diventerà <strong>Cessato subito</strong> (data odierna o passata).
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={closeStatusChange} disabled={statusChangeSubmitting} data-testid="status-change-cancel">
              {statusChange.target === 'cancel_schedule' ? 'Indietro' : 'Annulla'}
            </Button>
            <Button
              onClick={submitStatusChange}
              disabled={statusChangeSubmitting || (statusChange.target === 'terminated' && !statusChangeDate)}
              data-testid="status-change-confirm"
              className={
                statusChange.target === 'terminated' ? 'bg-gray-700 hover:bg-gray-800'
                : statusChange.target === 'suspended' ? 'bg-orange-600 hover:bg-orange-700'
                : statusChange.target === 'archived' ? 'bg-slate-600 hover:bg-slate-700'
                : 'bg-emerald-600 hover:bg-emerald-700'
              }
            >
              {statusChangeSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {statusChange.target === 'active' && 'Attiva'}
              {statusChange.target === 'suspended' && 'Sospendi'}
              {statusChange.target === 'archived' && 'Archivia'}
              {statusChange.target === 'terminated' && 'Conferma cessazione'}
              {statusChange.target === 'cancel_schedule' && 'Annulla cessazione'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Status Management Dialog */}
      <Dialog open={showStatusManager} onOpenChange={setShowStatusManager}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Gestisci Immobili</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Action Selection */}
            <div>
              <Label>Tipo di Operazione</Label>
              <Select value={statusAction} onValueChange={setStatusAction}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="close">Crea Periodo Chiusura</SelectItem>
                  <SelectItem value="terminate">Cessa Immobile</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Property Selection */}
            <div>
              <Label>Seleziona Immobile</Label>
              <Select value={selectedPropertyForStatus} onValueChange={setSelectedPropertyForStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Cerca e seleziona immobile..." />
                </SelectTrigger>
                <SelectContent>
                  {properties.filter(p => p.status === 'active').map(property => (
                    <SelectItem key={property.id} value={property.id}>
                      {property.name} - {property.city} ({property.property_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Closure Fields */}
            {statusAction === 'close' && (
              <div className="space-y-4">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    ℹ️ Il periodo di chiusura è temporaneo. L'immobile rimane ATTIVO con disponibilità limitata nelle date specificate.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Data Inizio Chiusura *</Label>
                    <Input
                      type="date"
                      value={closureData.startDate}
                      onChange={(e) => setClosureData({...closureData, startDate: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Data Fine Chiusura *</Label>
                    <Input
                      type="date"
                      value={closureData.endDate}
                      onChange={(e) => setClosureData({...closureData, endDate: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>Tipo Chiusura</Label>
                  <Select 
                    value={closureData.closureType} 
                    onValueChange={(value) => setClosureData({...closureData, closureType: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="property_initiated">Chiusura dalla Proprietà</SelectItem>
                      <SelectItem value="office_initiated">Chiusura dall'Ufficio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Motivo Chiusura (opzionale)</Label>
                  <Input
                    value={closureData.reason}
                    onChange={(e) => setClosureData({...closureData, reason: e.target.value})}
                    placeholder="Es: Manutenzione, Vacanze proprietario..."
                  />
                </div>
              </div>
            )}

            {/* Termination Fields */}
            {statusAction === 'terminate' && (
              <div>
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                  <p className="text-sm text-red-800">
                    ⚠️ La cessazione cambia permanentemente lo stato dell'immobile a CESSATO
                  </p>
                </div>
                <Label>Data Cessazione *</Label>
                <Input
                  type="date"
                  value={terminationDate}
                  onChange={(e) => setTerminationDate(e.target.value)}
                  required
                />
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowStatusManager(false)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button 
                onClick={statusAction === 'close' ? handleCreateClosure : handleTerminateProperty}
                className={`flex-1 ${statusAction === 'close' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-gray-600 hover:bg-gray-700'}`}
              >
                {statusAction === 'close' ? 'Crea Periodo Chiusura' : 'Cessa Immobile'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* XML Generator Dialog */}
      <Dialog open={xmlDialogOpen} onOpenChange={setXmlDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-green-600" />
              Genera XML ROSS1000
            </DialogTitle>
          </DialogHeader>
          
          {xmlProperty && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 border rounded-lg">
                <p className="font-medium">{xmlProperty.name}</p>
                <p className="text-sm text-slate-600">
                  {xmlProperty.property_code} • CIR: {xmlProperty.cir || 'N/A'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data Inizio</Label>
                  <Input
                    type="date"
                    value={xmlDateRange.start}
                    onChange={(e) => setXmlDateRange(prev => ({ ...prev, start: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data Fine</Label>
                  <Input
                    type="date"
                    value={xmlDateRange.end}
                    onChange={(e) => setXmlDateRange(prev => ({ ...prev, end: e.target.value }))}
                  />
                </div>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  Il file XML includerà arrivi, partenze e chiusure dell'immobile nel periodo selezionato.
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setXmlDialogOpen(false)} className="flex-1">
              Annulla
            </Button>
            <Button 
              onClick={downloadXml}
              disabled={generatingXml}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {generatingXml ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generazione...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Scarica XML
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicates Dialog */}
      <Dialog open={duplicatesDialogOpen} onOpenChange={setDuplicatesDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5" />
              Gestione Duplicati Immobili
            </DialogTitle>
          </DialogHeader>
          
          {duplicatesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-3">Analisi duplicati in corso...</span>
            </div>
          ) : duplicateGroups.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-800">Nessun Duplicato Trovato</h3>
              <p className="text-slate-500 mt-2">Il database degli immobili è pulito.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{duplicatesStats.totalGroups}</p>
                  <p className="text-xs text-amber-600">Gruppi</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{duplicatesStats.totalRecords}</p>
                  <p className="text-xs text-blue-600">Record Totali</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-700">{duplicatesStats.totalRecords - duplicatesStats.totalGroups}</p>
                  <p className="text-xs text-red-600">Da Eliminare</p>
                </div>
              </div>

              {/* Duplicate Groups */}
              {duplicateGroups.map((group, idx) => (
                <div key={idx} className="border border-amber-300 rounded-lg p-4 bg-amber-50/50">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <Badge className="bg-purple-100 text-purple-800 mr-2">
                        {group.match_type === 'name' ? 'Nome' : group.match_type === 'property_code' ? 'Codice' : 'Indirizzo'}
                      </Badge>
                      <span className="font-mono text-sm bg-white px-2 py-1 rounded">{group.match_value}</span>
                      <span className="text-sm text-slate-500 ml-2">({group.total_in_group} record)</span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleMergeDuplicates(
                        group.best_record.id,
                        group.duplicates.map(d => d.id),
                        group.best_record.name
                      )}
                      disabled={processingDuplicate === group.best_record.id}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {processingDuplicate === group.best_record.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Merge className="h-4 w-4 mr-1" />
                          Unisci e Pulisci
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Best Record */}
                  <div className="bg-green-100 border border-green-300 rounded p-2 mb-2">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="font-semibold text-green-800">Principale:</span>
                      <span>{group.best_record.name}</span>
                      <span className="text-slate-500">|</span>
                      <span className="font-mono text-xs">{group.best_record.property_code}</span>
                      <span className="text-slate-500">|</span>
                      <span className="text-xs">{group.best_record.city || 'N/A'}</span>
                      <Badge variant="outline" className="ml-auto">Score: {group.best_record.completeness_score}</Badge>
                    </div>
                  </div>
                  
                  {/* Duplicates */}
                  {group.duplicates.map((dup, dupIdx) => (
                    <div key={dupIdx} className="bg-red-100 border border-red-300 rounded p-2 mb-1">
                      <div className="flex items-center gap-2 text-sm">
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                        <span className="text-red-700">Duplicato:</span>
                        <span>{dup.name}</span>
                        <span className="text-slate-500">|</span>
                        <span className="font-mono text-xs">{dup.property_code}</span>
                        <span className="text-slate-500">|</span>
                        <span className="text-xs">{dup.city || 'N/A'}</span>
                        <Badge variant="outline" className="ml-auto bg-white">Score: {dup.completeness_score}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => fetchDuplicates()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Aggiorna
            </Button>
            <Button variant="outline" onClick={() => setDuplicatesDialogOpen(false)}>
              Chiudi
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Archived Properties Dialog */}
      <Dialog open={archivedDialogOpen} onOpenChange={setArchivedDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5" />
              Immobili Archiviati
            </DialogTitle>
          </DialogHeader>
          
          {archivedLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-3">Caricamento...</span>
            </div>
          ) : archivedProperties.length === 0 ? (
            <div className="text-center py-12">
              <Archive className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-800">Nessun Immobile Archiviato</h3>
              <p className="text-slate-500 mt-2">Gli immobili archiviati appariranno qui.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-slate-100 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-slate-700">{archivedProperties.length}</p>
                <p className="text-xs text-slate-500">Immobili Archiviati</p>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Immobile</th>
                      <th className="px-3 py-2 text-left">Codice</th>
                      <th className="px-3 py-2 text-left">Città</th>
                      <th className="px-3 py-2 text-left">Archiviato</th>
                      <th className="px-3 py-2 text-left">Motivo</th>
                      <th className="px-3 py-2 text-right">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archivedProperties.map((property) => (
                      <tr key={property.id} className="border-t hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium">{property.name}</td>
                        <td className="px-3 py-2 font-mono text-xs">{property.property_code}</td>
                        <td className="px-3 py-2">{property.city || '-'}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">
                          {property.archived_at ? new Date(property.archived_at).toLocaleDateString('it-IT') : '-'}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500 max-w-[150px] truncate" title={property.archive_reason}>
                          {property.archive_reason || '-'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleActivateProperty(property)}
                            disabled={archivingProperty === property.id}
                            className="h-7 text-green-600 hover:bg-green-50"
                          >
                            {archivingProperty === property.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <>
                                <Play className="h-3 w-3 mr-1" />
                                Attiva
                              </>
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => fetchArchivedProperties()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Aggiorna
            </Button>
            <Button variant="outline" onClick={() => setArchivedDialogOpen(false)}>
              Chiudi
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Suspended Properties Dialog */}
      <Dialog open={suspendedDialogOpen} onOpenChange={setSuspendedDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pause className="h-5 w-5 text-orange-500" />
              Immobili Sospesi
            </DialogTitle>
          </DialogHeader>
          
          {suspendedLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
              <span className="ml-3">Caricamento...</span>
            </div>
          ) : suspendedProperties.length === 0 ? (
            <div className="text-center py-12">
              <Pause className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-800">Nessun Immobile Sospeso</h3>
              <p className="text-slate-500 mt-2">Gli immobili sospesi temporaneamente appariranno qui.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-orange-100 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-orange-700">{suspendedProperties.length}</p>
                <p className="text-xs text-orange-600">Immobili Sospesi</p>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-orange-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Immobile</th>
                      <th className="px-3 py-2 text-left">Codice</th>
                      <th className="px-3 py-2 text-left">Città</th>
                      <th className="px-3 py-2 text-left">Sospeso il</th>
                      <th className="px-3 py-2 text-left">Motivo</th>
                      <th className="px-3 py-2 text-right">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suspendedProperties.map((property) => (
                      <tr key={property.id} className="border-t hover:bg-orange-50">
                        <td className="px-3 py-2 font-medium">{property.name}</td>
                        <td className="px-3 py-2 font-mono text-xs">{property.property_code}</td>
                        <td className="px-3 py-2">{property.city || '-'}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">
                          {property.suspended_at ? new Date(property.suspended_at).toLocaleDateString('it-IT') : '-'}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500 max-w-[150px] truncate" title={property.suspension_reason}>
                          {property.suspension_reason || '-'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleActivateProperty(property)}
                            disabled={archivingProperty === property.id}
                            className="h-7 text-green-600 hover:bg-green-50"
                          >
                            {archivingProperty === property.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <>
                                <Play className="h-3 w-3 mr-1" />
                                Attiva
                              </>
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => fetchSuspendedProperties()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Aggiorna
            </Button>
            <Button variant="outline" onClick={() => setSuspendedDialogOpen(false)}>
              Chiudi
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Association Dialog */}
      <Dialog open={associationDialogOpen} onOpenChange={setAssociationDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link className="h-5 w-5 text-blue-600" />
              Associazione Bulk Prenotazioni e Chiusure
            </DialogTitle>
          </DialogHeader>
          
          {associationLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-3">Caricamento statistiche...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Stats Cards */}
              {associationStats && (
                <div className="grid grid-cols-2 gap-4">
                  {/* Bookings Stats */}
                  <div className="border rounded-lg p-4 bg-blue-50">
                    <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Prenotazioni
                    </h4>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-white rounded p-2">
                        <p className="text-xl font-bold text-blue-700">{associationStats.bookings.total}</p>
                        <p className="text-xs text-slate-500">Totali</p>
                      </div>
                      <div className="bg-white rounded p-2">
                        <p className="text-xl font-bold text-green-600">{associationStats.bookings.associated}</p>
                        <p className="text-xs text-slate-500">Associate</p>
                      </div>
                      <div className="bg-white rounded p-2">
                        <p className="text-xl font-bold text-red-600">{associationStats.bookings.unassociated}</p>
                        <p className="text-xs text-slate-500">Non Associate</p>
                      </div>
                    </div>
                    <div className="mt-2 text-center">
                      <span className="text-sm text-blue-600 font-medium">
                        {associationStats.bookings.percentage_associated}% associato
                      </span>
                    </div>
                  </div>
                  
                  {/* Closures Stats */}
                  <div className="border rounded-lg p-4 bg-orange-50">
                    <h4 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
                      <X className="h-4 w-4" />
                      Chiusure
                    </h4>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-white rounded p-2">
                        <p className="text-xl font-bold text-orange-700">{associationStats.closures.total}</p>
                        <p className="text-xs text-slate-500">Totali</p>
                      </div>
                      <div className="bg-white rounded p-2">
                        <p className="text-xl font-bold text-green-600">{associationStats.closures.associated}</p>
                        <p className="text-xs text-slate-500">Associate</p>
                      </div>
                      <div className="bg-white rounded p-2">
                        <p className="text-xl font-bold text-red-600">{associationStats.closures.unassociated}</p>
                        <p className="text-xs text-slate-500">Non Associate</p>
                      </div>
                    </div>
                    <div className="mt-2 text-center">
                      <span className="text-sm text-orange-600 font-medium">
                        {associationStats.closures.percentage_associated}% associato
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Bulk Associate Button */}
              <div className="flex justify-center">
                <Button
                  onClick={handleBulkAssociate}
                  disabled={bulkAssociating}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {bulkAssociating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Associazione in corso...
                    </>
                  ) : (
                    <>
                      <Link className="h-4 w-4 mr-2" />
                      Esegui Associazione Bulk
                    </>
                  )}
                </Button>
              </div>

              {/* Unassociated Bookings Table */}
              <div className="border rounded-lg">
                <div className="bg-blue-100 border-b px-4 py-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Prenotazioni Non Associate ({unassociatedBookings.length})
                  </h4>
                </div>
                <div className="max-h-[250px] overflow-y-auto">
                  {unassociatedBookings.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Codice</th>
                          <th className="px-3 py-2 text-left">Ospite</th>
                          <th className="px-3 py-2 text-left">Check-in</th>
                          <th className="px-3 py-2 text-left">Check-out</th>
                          <th className="px-3 py-2 text-left">Piattaforma</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unassociatedBookings.slice(0, 100).map((booking, idx) => (
                          <tr key={booking.id || idx} className="border-t hover:bg-slate-50">
                            <td className="px-3 py-2 font-mono text-xs">{booking.booking_code}</td>
                            <td className="px-3 py-2">{booking.guest_name || '-'}</td>
                            <td className="px-3 py-2 text-xs">
                              {booking.check_in ? new Date(booking.check_in).toLocaleDateString('it-IT') : '-'}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {booking.check_out ? new Date(booking.check_out).toLocaleDateString('it-IT') : '-'}
                            </td>
                            <td className="px-3 py-2">
                              <Badge className="text-xs">{booking.platform || 'N/A'}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-6 text-center text-slate-500">
                      <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                      <p>Tutte le prenotazioni sono associate!</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Unassociated Closures Table */}
              <div className="border rounded-lg">
                <div className="bg-orange-100 border-b px-4 py-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <X className="h-4 w-4" />
                    Chiusure Non Associate ({unassociatedClosures.length})
                  </h4>
                </div>
                <div className="max-h-[250px] overflow-y-auto">
                  {unassociatedClosures.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Motivo</th>
                          <th className="px-3 py-2 text-left">Data Inizio</th>
                          <th className="px-3 py-2 text-left">Data Fine</th>
                          <th className="px-3 py-2 text-left">Tipo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unassociatedClosures.slice(0, 100).map((closure, idx) => (
                          <tr key={closure.id || idx} className="border-t hover:bg-slate-50">
                            <td className="px-3 py-2 max-w-[200px] truncate" title={closure.reason}>
                              {closure.reason || '-'}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {closure.start_date ? new Date(closure.start_date).toLocaleDateString('it-IT') : '-'}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {closure.end_date ? new Date(closure.end_date).toLocaleDateString('it-IT') : '-'}
                            </td>
                            <td className="px-3 py-2">
                              <Badge className={closure.closure_type === 'property_initiated' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}>
                                {closure.closure_type === 'property_initiated' ? 'Proprietà' : 'Ufficio'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-6 text-center text-slate-500">
                      <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                      <p>Tutte le chiusure sono associate!</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => openAssociationDialog()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Aggiorna
            </Button>
            <Button variant="outline" onClick={() => setAssociationDialogOpen(false)}>
              Chiudi
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Calendar Dialog (Bookings + Closures) */}
      <Dialog open={calendarDialogOpen} onOpenChange={setCalendarDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-green-600" />
              Calendario Prenotazioni e Chiusure
            </DialogTitle>
          </DialogHeader>
          
          {calendarProperty && (
            <div className="space-y-4">
              {/* Property Info */}
              <div className="p-3 bg-slate-50 border rounded-lg">
                <p className="font-medium">{calendarProperty.name}</p>
                <p className="text-sm text-slate-600">
                  {calendarProperty.property_code} • {calendarProperty.city}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={async () => {
                    setIsAssociatingBookings(true);
                    await handleAutoAssociateBookings(calendarProperty.id, calendarProperty.name);
                    // Refresh calendar after association
                    const token = localStorage.getItem('hw_token');
                    const response = await axios.get(`${API}/properties/${calendarProperty.id}/calendar`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    setCalendarData(response.data);
                    setIsAssociatingBookings(false);
                  }}
                  disabled={isAssociatingBookings}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isAssociatingBookings ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Associando...
                    </>
                  ) : (
                    <>
                      <Link className="h-4 w-4 mr-2" />
                      Associa Prenotazioni e Chiusure
                    </>
                  )}
                </Button>
              </div>

              {calendarLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                  <span className="ml-3">Caricamento...</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Bookings Section */}
                  <div className="border rounded-lg">
                    <div className="bg-blue-50 border-b p-3">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Prenotazioni ({calendarData.bookings?.length || 0})
                      </h4>
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                      {calendarData.bookings?.length > 0 ? (
                        <div className="divide-y">
                          {calendarData.bookings.map((booking, idx) => (
                            <div key={booking.id || idx} className="p-3 hover:bg-slate-50">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-sm">{booking.guest_name}</p>
                                  <p className="text-xs text-slate-500">{booking.booking_code}</p>
                                </div>
                                <Badge className={booking.cancelled ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                                  {booking.cancelled ? 'Cancellata' : 'Attiva'}
                                </Badge>
                              </div>
                              <div className="mt-2 text-xs text-slate-600">
                                📅 {new Date(booking.check_in).toLocaleDateString('it-IT')} → {new Date(booking.check_out).toLocaleDateString('it-IT')}
                                <span className="ml-2">👥 {booking.guests_count} ospiti</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-6 text-center text-slate-500">
                          <p>Nessuna prenotazione trovata</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Closures Section */}
                  <div className="border rounded-lg">
                    <div className="bg-orange-50 border-b p-3">
                      <h4 className="font-semibold flex items-center gap-2">
                        <X className="h-4 w-4" />
                        Chiusure ({calendarData.closures?.length || 0})
                      </h4>
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                      {calendarData.closures?.length > 0 ? (
                        <div className="divide-y">
                          {calendarData.closures.map((closure, idx) => (
                            <div key={closure.id || idx} className="p-3 hover:bg-slate-50">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-sm">
                                    {closure.closure_type === 'property_initiated' ? '🏠 Proprietà' : '🏢 Ufficio'}
                                  </p>
                                  <p className="text-xs text-slate-500">{closure.reason || 'Nessun motivo specificato'}</p>
                                </div>
                                <Badge className={closure.cancelled ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}>
                                  {closure.cancelled ? 'Annullata' : 'Attiva'}
                                </Badge>
                              </div>
                              <div className="mt-2 text-xs text-slate-600">
                                📅 {new Date(closure.start_date).toLocaleDateString('it-IT')} → {new Date(closure.end_date).toLocaleDateString('it-IT')}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-6 text-center text-slate-500">
                          <p>Nessuna chiusura trovata</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t">
                <div className="bg-blue-50 rounded p-2 text-center">
                  <p className="text-xl font-bold text-blue-700">{calendarData.bookings?.filter(b => !b.cancelled).length || 0}</p>
                  <p className="text-xs text-blue-600">Prenotazioni Attive</p>
                </div>
                <div className="bg-red-50 rounded p-2 text-center">
                  <p className="text-xl font-bold text-red-700">{calendarData.bookings?.filter(b => b.cancelled).length || 0}</p>
                  <p className="text-xs text-red-600">Prenotazioni Cancellate</p>
                </div>
                <div className="bg-orange-50 rounded p-2 text-center">
                  <p className="text-xl font-bold text-orange-700">{calendarData.closures?.filter(c => !c.cancelled).length || 0}</p>
                  <p className="text-xs text-orange-600">Chiusure Attive</p>
                </div>
                <div className="bg-slate-100 rounded p-2 text-center">
                  <p className="text-xl font-bold text-slate-700">{calendarData.closures?.filter(c => c.cancelled).length || 0}</p>
                  <p className="text-xs text-slate-600">Chiusure Annullate</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setCalendarDialogOpen(false)}>
              Chiudi
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Quick-Create Account di Gruppo ROSS1000 (apribile dalla scheda immobile) */}
      <Dialog open={quickGroupDialog.open} onOpenChange={(open) => setQuickGroupDialog({ open, propertyContext: open ? quickGroupDialog.propertyContext : null })}>
        <DialogContent className="max-w-lg" data-testid="quick-group-cred-dialog">
          <DialogHeader>
            <DialogTitle>➕ Crea Account di Gruppo ROSS1000/ISTAT</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
              <p>
                Le credenziali di gruppo sono <strong>condivise tra tutti gli immobili</strong> di una stessa provincia
                (es. tutti gli immobili di Genova usano lo stesso account regionale).
              </p>
              <p className="mt-1">
                Una volta creato, l'immobile in modifica userà automaticamente queste credenziali.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Regione *</Label>
                <Input
                  value={quickGroupForm.region}
                  onChange={(e) => setQuickGroupForm({ ...quickGroupForm, region: e.target.value })}
                  placeholder="es. Puglia"
                  data-testid="quick-group-region"
                />
              </div>
              <div>
                <Label>Provincia (nome) *</Label>
                <Input
                  value={quickGroupForm.province}
                  onChange={(e) => setQuickGroupForm({ ...quickGroupForm, province: e.target.value })}
                  placeholder="es. Taranto"
                  data-testid="quick-group-province"
                />
              </div>
            </div>

            <div>
              <Label>Codice Provincia (2 lettere) *</Label>
              <Input
                value={quickGroupForm.province_code}
                onChange={(e) => setQuickGroupForm({ ...quickGroupForm, province_code: e.target.value.toUpperCase().slice(0, 2) })}
                placeholder="es. TA"
                maxLength={2}
                data-testid="quick-group-province-code"
                className="uppercase"
              />
              <p className="text-xs text-slate-500 mt-1">Sigla automobilistica della provincia (TA, BA, NA, ecc.)</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Username ROSS1000 / GIES *</Label>
                <Input
                  value={quickGroupForm.username}
                  onChange={(e) => setQuickGroupForm({ ...quickGroupForm, username: e.target.value })}
                  placeholder="Username portale regionale"
                  data-testid="quick-group-username"
                />
              </div>
              <div>
                <Label>Password ROSS1000 / GIES *</Label>
                <Input
                  type="password"
                  value={quickGroupForm.password}
                  onChange={(e) => setQuickGroupForm({ ...quickGroupForm, password: e.target.value })}
                  placeholder="Password"
                  data-testid="quick-group-password"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setQuickGroupDialog({ open: false, propertyContext: null })}
              disabled={quickGroupSaving}
              data-testid="quick-group-cancel"
            >
              Annulla
            </Button>
            <Button
              onClick={submitQuickGroupCred}
              disabled={quickGroupSaving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="quick-group-save"
            >
              {quickGroupSaving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Salvataggio...</> : '💾 Crea Account'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Budget COSTS History Modal */}
      <Dialog open={!!showManagerHistory} onOpenChange={(open) => { if (!open) setShowManagerHistory(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="manager-history-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🕘 Storico responsabili sul posto
            </DialogTitle>
          </DialogHeader>
          {managerHistoryLoading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento storico...
            </div>
          ) : managerHistory.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <Archive className="h-8 w-8 mx-auto mb-2" />
              <p>Nessun responsabile storicizzato per questo immobile.</p>
              <p className="text-xs mt-1">Lo storico si popola automaticamente quando viene selezionato o cambiato il responsabile nella scheda immobile.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="manager-history-list">
              {managerHistory.map((entry, idx) => {
                const isCurrent = !entry.end_date;
                const fmtDate = (d) => d
                  ? new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                  : null;
                const dal = fmtDate(entry.start_date);
                const al = fmtDate(entry.end_date);
                return (
                  <Card key={entry.id || idx} className={`p-3 ${isCurrent ? 'border-emerald-300 bg-emerald-50/50' : ''}`} data-testid={`manager-history-row-${idx}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{entry.manager_name || '—'}</p>
                        <p className="text-xs text-slate-600 mt-0.5">
                          <span className="font-medium">Dal:</span> {dal || 'prima del tracciamento'}
                          <span className="mx-2">→</span>
                          <span className="font-medium">Al:</span> {al || (isCurrent ? '' : '—')}
                          {isCurrent && <Badge className="ml-1 bg-emerald-100 text-emerald-700">in corso</Badge>}
                        </p>
                        {entry.note && <p className="text-[11px] text-amber-700 mt-1">⚠️ {entry.note}</p>}
                      </div>
                      {entry.set_by && (
                        <span className="text-[11px] text-slate-400 whitespace-nowrap">impostato da {entry.set_by}</span>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!showBudgetHistory} onOpenChange={(open) => { if (!open) setShowBudgetHistory(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="budget-history-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              📜 Storico modifiche budget COSTS
            </DialogTitle>
          </DialogHeader>
          {budgetHistoryLoading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento storico...
            </div>
          ) : budgetHistory.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <Archive className="h-8 w-8 mx-auto mb-2" />
              <p>Nessuna modifica storicizzata per questo immobile.</p>
              <p className="text-xs mt-1">Lo storico viene popolato automaticamente quando HW Sync o un admin modifica budget/coefficiente.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="budget-history-list">
              {budgetHistory.map((entry, idx) => {
                const prevBudget = entry.previous_annual_budget;
                const newBudget = entry.new_annual_budget;
                const prevDaily = entry.previous_daily_incidence;
                const newDaily = entry.new_daily_incidence;
                const budgetChanged = prevBudget !== newBudget && (prevBudget !== undefined || newBudget !== undefined);
                const dailyChanged = prevDaily !== newDaily && (prevDaily !== undefined || newDaily !== undefined);
                const carryOver = entry.carry_over_from_previous_year;
                const yearLabel = entry.year;
                const changedAt = entry.changed_at
                  ? new Date(entry.changed_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                  : '—';
                const source = entry.source || 'manuale';
                const sourceLabel = source === 'hw_api' ? 'HW Sync' : (source === 'carry_over_calculation' ? 'Carry-over' : (source === 'manual' ? 'Manuale' : source));
                const sourceCls = source === 'hw_api'
                  ? 'bg-blue-100 text-blue-700'
                  : (source === 'carry_over_calculation' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-700');
                return (
                  <Card key={entry.id || idx} className="p-3" data-testid={`budget-history-row-${idx}`}>
                    <div className="flex items-center justify-between mb-2 text-xs">
                      <div className="flex items-center gap-2">
                        <Badge className={sourceCls}>{sourceLabel}</Badge>
                        {yearLabel && <span className="text-slate-500">Anno {yearLabel}</span>}
                      </div>
                      <span className="text-slate-500">{changedAt}</span>
                    </div>
                    {budgetChanged && (
                      <div className="text-sm flex items-center gap-2">
                        <span className="text-slate-600">Budget annuo COSTS:</span>
                        <span className="line-through text-slate-400">€ {Number(prevBudget || 0).toFixed(2)}</span>
                        <span className="text-slate-400">→</span>
                        <span className="font-semibold text-slate-900">€ {Number(newBudget || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {dailyChanged && (
                      <div className="text-sm flex items-center gap-2 mt-1">
                        <span className="text-slate-600">Coefficiente €/giorno:</span>
                        <span className="line-through text-slate-400">€ {Number(prevDaily || 0).toFixed(2)}</span>
                        <span className="text-slate-400">→</span>
                        <span className="font-semibold text-slate-900">€ {Number(newDaily || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {carryOver !== undefined && carryOver !== null && (
                      <div className="text-sm flex items-center gap-2 mt-1">
                        <span className="text-slate-600">Carry-over da anno precedente:</span>
                        <span className="font-semibold text-purple-700">€ {Number(carryOver || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {entry.notes && (
                      <div className="text-xs text-slate-500 mt-1 italic">{entry.notes}</div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
          <div className="flex justify-end pt-3 border-t">
            <Button variant="outline" onClick={() => setShowBudgetHistory(null)} data-testid="close-budget-history-btn">
              Chiudi
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {showCredAudit && (
        <div className="fixed inset-0 z-[70] bg-slate-900/60 flex items-center justify-center p-4" onClick={() => setShowCredAudit(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="cred-audit-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-slate-800">📜 Storico credenziali Questura / ISTAT</h3>
              <button onClick={() => setShowCredAudit(false)} className="text-slate-400 hover:text-slate-600 text-xl" data-testid="cred-audit-close">×</button>
            </div>
            {credAuditLogs.length === 0 ? (
              <p className="text-sm text-slate-500">Nessuna azione registrata per questo immobile (il registro traccia le azioni dal 24/07/2026 in poi).</p>
            ) : (
              <div className="space-y-2">
                {credAuditLogs.map((l) => (
                  <div key={l.id} className="border border-slate-200 rounded-lg p-3 text-xs" data-testid="cred-audit-entry">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full font-semibold ${l.action === 'rimozione_bloccata' ? 'bg-red-100 text-red-700' : l.action === 'inserimento' ? 'bg-emerald-100 text-emerald-700' : l.action === 'cancellazione' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {l.action === 'rimozione_bloccata' ? '🛡️ svuotamento bloccato' : l.action}
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 uppercase">{l.credential_type}</span>
                      <span className="text-slate-500">{new Date(l.timestamp).toLocaleString('it-IT')}</span>
                      <span className="font-medium text-slate-700">{l.user_email}</span>
                      <span className="text-slate-400">({l.source})</span>
                    </div>
                    {l.changes && Object.keys(l.changes).length > 0 && (
                      <div className="text-slate-600">
                        {Object.entries(l.changes).map(([f, c]) => (
                          <div key={f}>{f}: <em>{c.da || '—'}</em> → <strong>{c.a || '—'}</strong></div>
                        ))}
                      </div>
                    )}
                    {l.note && <div className="text-red-600 mt-1">{l.note}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

    </Layout>
  );
};

export default PropertiesManager;